import { Injectable, NgModule } from '@angular/core';
import { DataApiService, BaseService } from '@xpo-ltl/data-api';
import { ConfigManagerService, CONFIG_MANAGER_SERVICE_CONFIGURATION, ConfigManagerModule } from '@xpo-ltl/config-manager';
import { PayloadType } from '@xpo-ltl/sdk-common';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */
import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@xpo-ltl/data-api';
import * as ɵngcc2 from '@xpo-ltl/config-manager';
class EquipmentLicensingApiService extends BaseService {
    /**
     * @param {?} dataApiService
     * @param {?} configManager
     */
    constructor(dataApiService, configManager) {
        super();
        this.dataApiService = dataApiService;
        this.configManager = configManager;
    }
    /**
     * Health check URL. Responds with success message if the service is running.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    healthCheck(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/health-check'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List of resources.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    options(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/options'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Details of user invoking the API.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    loggedInUser(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/appusers/logged-in-user'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation creates or updates Equipment records in the LIC_EQUIPMENT table and returns the Equipment records that were successful.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. Valid Equipment information must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Equipment records are upserted and the operation returns the Equipment records that were successful.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    bulkUpsertEquipment(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/equipment/bulk-upsertion'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation compare the requirements of an old License Code with a new one.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid oldLicenseCd and newLicenseCd are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the comparison is successful, then the following is returned:
     * <br/>  - The description of both License Codes.
     * <br/>  - The list of License Code Requirements that are linked to the old License Code.
     * <br/>  - The list of License Code Requirements that are linked to the new License Code.
     * <br/>  - The list of License Code Requirements that the new License Code has which the old one does not.
     * <br/>  - The list of old State Weight Groups.
     * <br/>  - The list of new State Weight Groups.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    compareLicenseCodes(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/license-code-comparisons'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation compares two State Weight Groups.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid oldWeightGroupCd and newWeightGroupCd are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the comparison is successful, then the list of weight comparison results for the states is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    compareStateWeightGroups(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/state-weight-group-comparisons'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a new Corp based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid Corp is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Corp record is created and returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createCorp(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/corps'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a Corp Code Requirement Project based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. Valid CorpCodeRequirementProject details are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Corp Code Requirement Project record is created.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createCorpCodeRequirementProject(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirement-projects/corp-code-requirement-projects'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a new equipment record in the LIC_EQUIPMENT table.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. From the entity received, the mandatory fields must exist and be valid. The optional fields must be valid if provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the input is valid, then the equipment record is created.
     * <br/>2. If invalid, then an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createEquipment(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/equipment'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates comments for a list of Equipment records that can be identified by their equipmentNbrs.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A list of valid EquipmentComments is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the list of EquipmentComment records will be created.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createEquipmentComments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/equipment-comments'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a new Fleet Code based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid FleetCode is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Fleet Code record is created and returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createFleetCd(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/fleet-codes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a new License Code based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid LicenseCode is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the License Code record is created and returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createLicenseCd(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/license-codes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a new Requirement based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid Requirement is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Requirement record is created and returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createRequirement(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirements'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a new Requirement Comment based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid RequirementComment is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Requirement Comment record is created and returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createRequirementComment(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirement-comments'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a Requirement Project based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid requirementCd and corpCd are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Requirement Project record is created.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createRequirementProject(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirement-projects'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates an UploadControlCsv record in the LIC_UPLOAD_CNTL_CSV table. These records contain the control information for CSV file uploads for mass Licensing Equipment updates.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid UploadControlCsv record is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the UploadControlCsv record is created and returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createUploadControlCsv(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/csv-upload-controls'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates UploadCsvRecError records in the LIC_UPLOAD_CSV_REC_ERROR table. These records contain information about the errors encountered while uploading a CSV file.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. At least one valid UploadCsvRecError record is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the UploadCsvRecError records are created and returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createUploadCsvRecErrors(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/csv-upload-errors'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation deletes an Equipment record by its equipment number.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid equipment
     * <br/>
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteEquipment(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/equipment/{equipmentNbr}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * This operation deletes an Equipment Comment record by its equipment number and comment sequence number.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid equipmentNbr and sequenceNbr are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then an EquipmentComment record is deleted.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteEquipmentComment(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/equipment/{equipmentNbr}/comments/{sequenceNbr}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * This operation deletes a Fleet Code record by its fleetCd.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid fleetCd is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Fleet Code record is deleted.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteFleetCd(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/fleet-codes/{fleetCd}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * This operation deletes a License Code record by its licenseCd.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid licenseCd is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the License Code record is deleted.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteLicenseCd(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/license-codes/{licenseCd}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * This operation deletes a Requirement record by its requirementCd.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid requirementCd is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Requirement record is deleted.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteRequirement(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirements/{requirementCd}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * This operation deletes an existing Requirement Comment based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid requirementCd and seqNbr are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Requirement Comment record is deleted.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteRequirementComment(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirements/{requirementCd}/comments/{seqNbr}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * This operation deletes an existing Requirement Project based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid requirementProjectId is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Requirement Project record is deleted.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteRequirementProject(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirement-projects/{requirementProjectId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * This operation gets the Domicile State and Weight Groups from the associated license code.
     * <br/>
     * <br/>A license code could be associated with multiple requirement codes, but the way that the user is currently assigning a weight group to a license code is by using the requirement codes.
     * <br/>
     * <br/>Example:
     * <br/>License code number 3321's domicile state is OR and has the following requirements: A, B, C, W3, D. In this case, the information that needs to be captured is the domicile state (OR) and weight group (W3) to proceed with the validation on the equipment form.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid licenseCd must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If there are Domicile State and Weight Groups associated with the licensed provided, then they will be returned.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getDomicileStateAndWeightGroups(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/licenses/{licenseCd}/domicile-state-and-weight-groups'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves an Equipment record based on the equipment number provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. The equipmentNbr is provided and valid.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Equipment record is retrieved.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getEquipment(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/equipment/{equipmentNbr}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a equipment comment based on the equipment number and comment sequence number provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. The equipmentNbr and sequenceNbr are provided and valid.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If sucessful, then the EquipmentComment record is retrieved.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getEquipmentComment(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/equipment/{equipmentNbr}/comments/{sequenceNbr}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a Corp (LIC_CORP table) and determines if the corpCd provided is valid.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid corpCd is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the input is valid, then the associated Corp is returned along with a boolean indicator that determines the corpCd's validity.
     * <br/>2. If the input is invalid, then an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getCorp(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/corps/{corpCd}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a Corp Code Requirement Project and determines whether the associated corpCodeRequirementProjectId provided is valid.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid corpCodeRequirementProjectId is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the associated Corp Code Requirement Project record is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getCorpCodeRequirementProject(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirement-projects/corp-code-requirement-projects/{corpCodeRequirementProjectId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a Fleet Code.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid fleetCd is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the associated Fleet Code is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getFleetCd(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/fleet-codes/{fleetCd}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a License Code and determines whether the licenseCd provided is valid.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid licenseCd is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the associated LicenseCode and LicenseCodeStates are returned along with a boolean indicator that determines the licenseCd's validity.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getLicenseCd(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/license-codes/{licenseCd}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a Requirement.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid requirementCd is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the associated Requirement is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getRequirement(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirements/{requirementCd}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a Requirement Project based on the requirement and corp codes provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid requirementProjectId is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Requirement Project record is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getRequirementProject(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirement-projects/{requirementProjectId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a list of all existing Corps.
     * <br/>
     * <br/>Pre-condition:
     * <br/>None
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of Corp records is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listCorps(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/corps'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a list of equipment based on the filter criteria provided, if any.
     * <br/>
     * <br/>Pre-condition:
     * <br/>None
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then a list of Equipment records is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listEquipment(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/equipment-lists'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation retrieves all EquipmentComment records associated with the equipment number provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. The equipmentNbr must be provided and valid.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then all EquipmentComment records associated with the equipmentNbr provided are listed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listEquipmentComments(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/equipment/{equipmentNbr}/comments'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists the changes on a field for an Equipment record.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. The equipmentNbr and fieldName are provided and valid.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Equipment field history records are listed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listEquipmentFieldChangeHistory(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/equipment/{equipmentNbr}/fields/{fieldName}/history'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the Equipment History records associated with an equipment number.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. The equipmentNbr is provided and valid.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Equipment History records are listed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listEquipmentHistory(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/equipment/{equipmentNbr}/history'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a list of Fleet Codes based on the filter criteria provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>None
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of Fleet Code records that matched the filter criteria is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listFleetCds(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/fleet-code-lists'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation retrieves the License Code History records associated with a license code.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid licenseCd is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the License Code History records are listed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listHistoryForLicenseCd(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/license-codes/{licenseCd}/history'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists the changes on a field for a License Code record.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid licenseCd and fieldName are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the License Code field history records are listed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listHistoryForLicenseCdFieldChange(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/license-codes/{licenseCd}/fields/{fieldName}/history'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a list of Requirement Projects that match the following criteria:
     * <br/>
     * <br/>-The current date matches either the first reminder date or the second reminder date of the project.
     * <br/>-The completion date is either null or from the previous year.
     * <br/>
     * <br/>Pre-condition:
     * <br/>None
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the list of incomplete Requirement Projects is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listIncompleteRequirementProjects(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirement-projects/incomplete-requirement-projects'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a list of License Codes based on the filter criteria provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. At least one valid licenseCd is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of License Code records that matched the filter criteria is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listLicenseCds(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/license-code-lists'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation retrieves all RequirementComment records associated with the requirement code provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid requirementCd must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then all RequirementComment records associated with the requirementCd provided are listed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listRequirementComments(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirements/{requirementCd}/comments'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a list of Requirements based on the filter criteria provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>None
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of Requirement records that matched the filter criteria is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listRequirements(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirement-lists'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation retrieves a list of Requirement Projects based on the filter criteria provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid requirementCd is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of Requirement Project records that matched the filter criteria is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listRequirementProjects(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirements/{requirementCd}/projects'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists the State Weight Group headers.
     * <br/>
     * <br/>Pre-condition:
     * <br/>None
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of the State Weight Groups headers is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listStateWeightGroupHeaders(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/state-weight-group-headers'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists the State Weight Groups.
     * <br/>
     * <br/>Pre-condition:
     * <br/>None
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of the State Weight Groups is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listStateWeightGroups(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/state-weight-groups'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the list of UploadControlCsv records from the LIC_UPLOAD_CNTL_CSV table.
     * <br/>
     * <br/>Pre-condition:
     * <br/>None
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the list of UploadControlCsv records is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listUploadControlCsvs(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/csv-upload-controls'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists the CSV Recordable Error records for a CSV upload.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid upload ID is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the list of error records for the upload is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listUploadCsvRecErrors(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/csv-upload-rec-errors/{uploadId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation updates an existing Corp Code Requirement Project based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. Valid CorpCodeRequirementProject details are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Corp Code Requirement Project record is updated and a message with the result of the update is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateCorpCodeRequirementProject(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirement-projects/corp-code-requirement-projects'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates an equipment record using one or many equipment numbers (up to 3000) in the LIC_EQUIPMENT table.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. From the list of equipment numbers received, should exist at least one.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If the input is valid, then the equipment record is
     * <br/>updated.
     * <br/>2. If invalid, then an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateEquipment(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/equipment'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates an EquipmentComment record that can be identified by the equipmentNbr and the sequenceNbr.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. The EquipmentComment fields are provided and valid.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the existing EquipmentComment record will be updated.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateEquipmentComment(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/equipment-comments'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates an existing Fleet Code based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid FleetCode is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Fleet Code record is updated and a message with the result of the update is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateFleetCd(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/fleet-codes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates existing License Codes based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. At lease one valid licenseCd along with LicenseCode details are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the License Code records are updated and a message with the result of the update is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateLicenseCds(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/license-codes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates an existing Requirement based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid Requirement is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Requirement record is updated and a message with the result of the update is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateRequirement(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirements'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates an existing Requirement Comment based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid RequirementComment is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Requirement Comment record is updated and a message with the result of the update is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateRequirementComment(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirement-comments'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates an existing Requirement Project based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid RequirementProject is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Requirement Project record is updated and a message with the result of the update is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateRequirementProject(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/requirement-projects'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates existing State Weight Groups based on the information provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. At lease one valid StateWeightGroup is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the weight limits for the State Weight Group records are updated and a message with the result of the update is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateStateWeightGroups(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/state-weight-groups'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates an UploadControlCsv record in the LIC_UPLOAD_CNTL_CSV table.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid UploadControlCsv record is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the UploadControlCsv record is updated and returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateUploadControlCsv(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/csv-upload-controls'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation validates license codes.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. At least one licenseCd is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of license codes is returned after validation was performed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    validateLicenseCds(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/license-codes/validations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation restarts a STARTED, NOT_STARTED, LOCK_WAIT_FAILED Ensemble execution based on input Ensemble Instance Id.
     * <br/>Pre-conditions:
     * <br/>1. Input Ensemble Instance Id List is supplied and valid.
     * <br/>Post-condition:
     * <br/>1. STARTED, NOT_STARTED, LOCK_WAIT_FAILED Ensemble execution status is updated to STARTED and execution is initiated; if any Minuets exist for the Ensemble, those are initiated in sequence and status updates are performed.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    bulkRestartEnsemble(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/ensembles/bulkRestart'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation Resumes a SUSPENDED Ensemble execution based on input Ensemble Instance Id.
     * <br/>Pre-conditions:
     * <br/>1. Input Ensemble Instance Id List is supplied and valid.
     * <br/>Post-condition:
     * <br/>1. SUSPENDED Ensemble execution status is updated to STARTED and execution is initiated; if any Minuets exist for the Ensemble, those are initiated in sequence and status updates are performed.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    bulkResumeEnsemble(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/ensembles/bulkResume'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Delete expired messages in the queue
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteExpiredMessages(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/messages'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * This operation returns counts for the number of ensembles in each of the status cd values.
     * <br/>Pre-conditions:
     * <br/>1. Input search criteria may be provided.
     * <br/>Post-condition:
     * <br/>1. Counts for each ensemble status are returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getEnsembleStatusCounts(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/counts/{fromTmst}/{toTmst}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns counts for PubSub statuses
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getPubSubStatusCounts(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/{subscriptionId}/summary/counts/{fromTmst}/{toTmst}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns counts for the number of messages pending in the queue.
     * <br/>Pre-conditions:
     * <br/>1. Queue name is provided.
     * <br/>Post-condition:
     * <br/>1. Count of all messages in ready and wait statuses
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getQueuePendingCount(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/counts/pending'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation return Status of Given Subscription
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getSubscription(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/{subscriptionId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns a count of number of undelivered messages in this PubSub subscription
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getUndeliveredMessages(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/{subscriptionId}/undelivered/counts'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation updates one or more SUSPENDED Ensemble execution row to a status of KILLED, in order to indicate it is has been forced to stop processing.
     * <br/>Pre-conditions:
     * <br/>1. One or more Ensemble Instance Ids that is in a status of SUSPENDED is supplied and valid.
     * <br/>Post-condition:
     * <br/>1. Ensemble record(s) updated to a Status of KILLED.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    killEnsemble(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/ensembles/kill'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * List the channels.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listChannels(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/channels/'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List all expired messages in the queue
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listExpiredMessages(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/messages/{enqueueFromTmst}/{enqueueToTmst}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List the queues.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listQueues(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List the Pub/Sub subscriptions.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listSubscriptions(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Replay a message in expired status
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    replayMessage(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/messages/{messageId}'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation Restarts a STARTED or NOT_STARTED Ensemble execution based on input Ensemble Instance Id.
     * <br/>Pre-conditions:
     * <br/>1. Input Ensemble Instance Id is supplied and valid.
     * <br/>Post-condition:
     * <br/>1. STARTED or NOT_STARTED Ensemble execution status is updated to STARTED and execution is initiated; if any Minuets exist for the Ensemble, those are initiated in sequence and status updates are performed.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    restartEnsemble(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/ensembles/{ensembleInstId}/restart'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation Resumes a SUSPENDED Ensemble execution based on input Ensemble Instance Id.
     * <br/>Pre-conditions:
     * <br/>1. Input Ensemble Instance Id is supplied and valid.
     * <br/>Post-condition:
     * <br/>1. SUSPENDED Ensemble execution status is updated to STARTED and execution is initiated; if any Minuets exist for the Ensemble, those are initiated in sequence and status updates are performed.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    resumeEnsemble(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/ensembles/{ensembleInstId}/resume'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Start an API Channel.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startChannel(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/channels/{channelName}/start'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Start a queue.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startQueue(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/start'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Start or restart a PubSub subscription.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startSubscription(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/{subscriptionId}/start'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Stop an API Channel.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    stopChannel(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/channels/{channelName}/stop'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Stop a queue. Stopping a queue will only stop the dequeuing process, it will continue to enqueue
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    stopQueue(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/stop'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Stop a queue. Stopping a queue will only stop the dequeuing process, it will continue to enqueue
     * @param {?} request
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    stopQueueWebhook(request, pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/webhooks/queues/{queueName}/stop'), {
            pathParams: pathParams,
            queryParams: queryParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Stop a PubSub subscription.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    stopSubscription(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/{subscriptionId}/stop'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for ensemble-state-unfiltered
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for EnsembleStateUnfiltered
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for EnsembleStateUnfiltered
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Delete Elastic document for EnsembleState
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteElasticDocumentEnsembleState(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/ensemble-state-unfiltered/_doc/{instId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * Get Elastic document from EnsembleState index for given primary key
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getElasticDocumentEnsembleState(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/ensemble-state-unfiltered/_doc/{instId}'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Get document count from EnsembleState index
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getElasticDocumentEnsembleStateCount(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/ensemble-state-unfiltered/_count'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Update Elastic document for EnsembleState
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateElasticDocumentEnsembleState(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/ensemble-state-unfiltered/_doc/{instId}/_update'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * List all ensemble configurations
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listEnsembleConfigurations(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/configurations'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List the timers.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listTimerEnsembles(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/timer-ensembles/{name}/timers'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Start a timer.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startTimerEnsemble(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/timers/{name}'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Stop a timer.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    stopTimerEnsemble(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/timers/{name}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * Start ensemble by invoking API
     * <br/>        	PRE-CONDITION:
     * <br/>        		valid channelName is required
     * <br/>        		valid payload is required
     * <br/>        	POST-CONDITION:
     * <br/>        		Ensemble is started and an id is returned in the response
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startLicMaintChEnsemble(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/channels/LIC_MAINT_CH/ensembles'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * @return {?}
     */
    getEndPoint() {
        return this.configManager.getSetting(EquipmentLicensingApiService.EquipmentLicensingApiEndpoint);
    }
}
EquipmentLicensingApiService.ɵfac = function EquipmentLicensingApiService_Factory(t) { return new (t || EquipmentLicensingApiService)(ɵngcc0.ɵɵinject(ɵngcc1.DataApiService), ɵngcc0.ɵɵinject(ɵngcc2.ConfigManagerService)); };
EquipmentLicensingApiService.ɵprov = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjectable({ token: EquipmentLicensingApiService, factory: EquipmentLicensingApiService.ɵfac });
EquipmentLicensingApiService.EquipmentLicensingApiEndpoint = 'equipmentlicensingApiEndpoint';
/** @nocollapse */
EquipmentLicensingApiService.ctorParameters = () => [
    { type: DataApiService },
    { type: ConfigManagerService }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(EquipmentLicensingApiService, [{
        type: Injectable
    }], function () { return [{ type: ɵngcc1.DataApiService }, { type: ɵngcc2.ConfigManagerService }]; }, null); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */
const ɵ0 = { configFileUrl: './assets/config.json' };
class SdkEquipmentLicensingModule {
}
SdkEquipmentLicensingModule.ɵfac = function SdkEquipmentLicensingModule_Factory(t) { return new (t || SdkEquipmentLicensingModule)(); };
SdkEquipmentLicensingModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: SdkEquipmentLicensingModule });
SdkEquipmentLicensingModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({ providers: [
        EquipmentLicensingApiService,
        { provide: CONFIG_MANAGER_SERVICE_CONFIGURATION, useValue: ɵ0 },
    ], imports: [[
            ConfigManagerModule
        ]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(SdkEquipmentLicensingModule, [{
        type: NgModule,
        args: [{
                imports: [
                    ConfigManagerModule
                ],
                providers: [
                    EquipmentLicensingApiService,
                    { provide: CONFIG_MANAGER_SERVICE_CONFIGURATION, useValue: ɵ0 },
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(SdkEquipmentLicensingModule, { imports: function () { return [ConfigManagerModule]; } }); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */
/**
 * Request to bulk resume ensembles.
 */
class BulkRestartEnsembleRqst {
    constructor() {
        /**
         * Ensemble id list to be restarted
         * Required: true
         * Array: true
         */
        this.ensembleIds = undefined;
    }
}
/**
 * Response to the Request to Restart execution of a Started, NoStarted and LockWaitFailed ensemble
 */
class BulkRestartEnsembleResp {
    constructor() {
        /**
         * Ensemble that was Restarted
         * Required: false
         * Array: false
         */
        this.ensembleRestartedInd = undefined;
    }
}
/**
 * Request to bulk resume ensembles.
 */
class BulkResumeEnsembleRqst {
    constructor() {
        /**
         * Ensemble id list to be resumed
         * Required: true
         * Array: true
         */
        this.ensembleIds = undefined;
    }
}
/**
 * Response to the Request to Resume execution of a Suspended ensemble
 */
class BulkResumeEnsembleResp {
    constructor() {
        /**
         * Ensemble that was Resumed
         * Required: false
         * Array: false
         */
        this.ensembleResumedInd = undefined;
    }
}
/**
 * Path parameters for get Ensemble status counts
 */
class GetEnsembleStatusCountsPath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.queueName = undefined;
        /**
         * From timestamp
         * Required: true
         * Array: false
         */
        this.fromTmst = undefined;
        /**
         * To timestamp
         * Required: true
         * Array: false
         */
        this.toTmst = undefined;
    }
}
/**
 * Response to the request to get Ensemble status counts
 */
class GetEnsembleStatusCountsResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.ensembleMetrics = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.queueMetrics = undefined;
    }
}
/**
 * Path parameters for get Ensemble status counts
 */
class GetQueuePendingCountPath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Response for getQueuePendingCount
 */
class GetQueuePendingCountResp {
    constructor() {
        /**
         * Number of messages in either ready or wait status. This count indicates the queue depth.
         * Required: false
         * Array: false
         */
        this.pendingCount = undefined;
    }
}
/**
 * Request to Restart execution of a Started or Not Started  Ensemble
 */
class RestartEnsemblePath {
    constructor() {
        /**
         * Name of the queue
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
        /**
         * Unique identifier for the application workflow.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
    }
}
/**
 * Request to Restart execution of a Started or Not Started  Ensemble
 */
class RestartEnsembleRqst {
}
/**
 * Response to the Request to Restart execution of a Started or Not Started  Ensemble
 */
class RestartEnsembleResp {
    constructor() {
        /**
         * Ensemble that was Restarted.
         * Required: false
         * Array: false
         */
        this.ensembleRestartedInd = undefined;
    }
}
/**
 * Request to Resume execution of a Suspended ensemble
 */
class ResumeEnsemblePath {
    constructor() {
        /**
         * Name of the queue
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
        /**
         * Unique identifier for the application workflow.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
    }
}
/**
 * Request to Resume execution of a Suspended ensemble
 */
class ResumeEnsembleRqst {
}
/**
 * Response to the Request to Resume execution of a Suspended ensemble
 */
class ResumeEnsembleResp {
    constructor() {
        /**
         * Ensemble that was Resumed
         * Required: false
         * Array: false
         */
        this.ensembleResumedInd = undefined;
    }
}
/**
 * Request to Resume execution of a Suspended ensemble
 */
class BulkResumeEnsemblePath {
    constructor() {
        /**
         * Name of the queue
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Request to Restart execution of a Started, NotStarted or LockWaitFailed ensemble
 */
class BulkRestartEnsemblePath {
    constructor() {
        /**
         * Name of the queue
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Request to start ensemble.
 */
class StartMessageDrivenEnsembleRqst {
    constructor() {
        /**
         * Required: true
         * Array: false
         */
        this.queue = undefined;
        /**
         * Required: true
         * Array: false
         */
        this.messageId = undefined;
        /**
         * Required: true
         * Array: false
         */
        this.message = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.eventDataType = undefined;
    }
}
/**
 * Enter comments
 */
class StartMessageDrivenEnsembleResp {
    constructor() {
        /**
         * Instance ID of the ensemble that was started.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
    }
}
/**
 * Request to stop execution of an Ensemble
 */
class KillEnsembleRqst {
    constructor() {
        /**
         * Unique identifier for the ensemble.
         * Required: true
         * Array: true
         */
        this.ensembleInstIds = undefined;
    }
}
/**
 * Response to the request to stop execution of an Ensemble
 */
class KillEnsembleResp {
    constructor() {
        /**
         * Ensembles that were killed.
         * Required: false
         * Array: true
         */
        this.status = undefined;
    }
}
/**
 * Path parameters for stopQueue
 */
class StopQueuePath {
    constructor() {
        /**
         * Name of the queue to stop
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Path parameters for stopQueue
 */
class StopQueueWebhookPath {
    constructor() {
        /**
         * Name of the queue to stop
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Path parameters for stopQueue
 */
class StopQueueWebhookQuery {
    constructor() {
        /**
         * Authorization token
         * Required: false
         * Array: false
         */
        this.auth_token = undefined;
    }
}
/**
 * Path parameters for startQueue
 */
class StartQueuePath {
    constructor() {
        /**
         * Name of the queue to start
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Response for listQueues
 */
class ListQueuesResp {
    constructor() {
        /**
         * Name of the queue to start
         * Required: false
         * Array: true
         */
        this.queues = undefined;
    }
}
/**
 * Path parameters
 */
class ListExpiredMessagesPath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.queueName = undefined;
        /**
         * From enqueue timestamp
         * Required: true
         * Array: false
         */
        this.enqueueFromTmst = undefined;
        /**
         * Tom enqueue timestamp
         * Required: true
         * Array: false
         */
        this.enqueueToTmst = undefined;
    }
}
/**
 * Response
 */
class ListExpiredMessagesResp {
    constructor() {
        /**
         * Name of the queue
         * Required: false
         * Array: true
         */
        this.message = undefined;
    }
}
/**
 * Path parameters
 */
class DeleteExpiredMessagesPath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Path parameters
 */
class DeleteExpiredMessagesQuery {
    constructor() {
        /**
         * Id of the message to be deleted
         * Required: true
         * Array: true
         */
        this.messageId = undefined;
    }
}
/**
 * Response to the request to stop execution of an Ensemble
 */
class DeleteExpiredMessagesResp {
    constructor() {
        /**
         * Ensembles that were killed.
         * Required: false
         * Array: true
         */
        this.status = undefined;
    }
}
/**
 * Path parameters
 */
class ReplayMessagePath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.queueName = undefined;
        /**
         * ID of the message to replay. It needs to be in expired status
         * Required: true
         * Array: false
         */
        this.messageId = undefined;
    }
}
/**
 * Path parameters for get Ensemble status counts
 */
class GetPubSubStatusCountsPath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.subscriptionId = undefined;
        /**
         * From timestamp
         * Required: true
         * Array: false
         */
        this.fromTmst = undefined;
        /**
         * To timestamp
         * Required: true
         * Array: false
         */
        this.toTmst = undefined;
    }
}
/**
 * Response to the request to get Ensemble status counts
 */
class GetPubSubStatusCountsResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pubsubMetrics = undefined;
    }
}
/**
 * Path parameters for startSubscription
 */
class StartSubscriptionPath {
    constructor() {
        /**
         * Id of the subscription to start
         * Required: false
         * Array: false
         */
        this.subscriptionId = undefined;
    }
}
/**
 * Path parameters for stopSubscription
 */
class StopSubscriptionPath {
    constructor() {
        /**
         * Id of the subscription to stop
         * Required: false
         * Array: false
         */
        this.subscriptionId = undefined;
    }
}
/**
 * Path parameters for get Undelivered Message counts
 */
class GetUndeliveredMessagesPath {
    constructor() {
        /**
         * Id of subscription
         * Required: true
         * Array: false
         */
        this.subscriptionId = undefined;
    }
}
/**
 * Response for getUndeliveredMessages
 */
class GetUndeliveredMessagesResp {
    constructor() {
        /**
         * Number of Undelivered Messages.
         * Required: false
         * Array: false
         */
        this.undeliveredCount = undefined;
    }
}
/**
 * Response for listSubscriptions
 */
class ListSubscriptionsResp {
    constructor() {
        /**
         * Name of the queue to start
         * Required: false
         * Array: true
         */
        this.subscriptions = undefined;
    }
}
/**
 * Path parameters for Get subscription count
 */
class GetSubscriptionPath {
    constructor() {
        /**
         * Subscription id
         * Required: true
         * Array: false
         */
        this.subscriptionId = undefined;
    }
}
/**
 * Returns status of a given subscription
 */
class GetSubscriptionResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.subscription = undefined;
    }
}
/**
 * Path parameters for startChannel
 */
class StartChannelPath {
    constructor() {
        /**
         * Name of the channel to start
         * Required: false
         * Array: false
         */
        this.channelName = undefined;
    }
}
/**
 * Path parameters for stopChannel
 */
class StopChannelPath {
    constructor() {
        /**
         * Name of the channel to stop
         * Required: false
         * Array: false
         */
        this.channelName = undefined;
    }
}
/**
 * Response for listChannels
 */
class ListChannelsResp {
    constructor() {
        /**
         * list of channels
         * Required: false
         * Array: true
         */
        this.channels = undefined;
    }
}
class MessageDrivenEnsemblePayload {
    constructor() {
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventType = undefined;
        /**
         * The event occurrence timestamp
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The event occurrence timestamp in microseconds
         * Required: false
         * Array: false
         */
        this.eventTmstMicro = undefined;
        /**
         * The correlation ID of the originating transaction
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * The event data. Use JSON format with previous and current values of all attributes that were changed by this event
         * Required: false
         * Array: false
         */
        this.eventData = undefined;
    }
}
class DynamicMessageDrivenPayload {
    constructor() {
        /**
         * The type of the payload
         * Required: true
         * Array: false
         */
        this.eventDataType = undefined;
        /**
         * The message payload
         * Required: true
         * Array: false
         */
        this.payload = undefined;
    }
}
class TransactionEventEnsemblePayload {
    constructor() {
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventType = undefined;
        /**
         * The event occurrence timestamp
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The event occurrence timestamp in microseconds
         * Required: false
         * Array: false
         */
        this.eventTmstMicro = undefined;
        /**
         * The correlation ID of the originating transaction
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * The event data. Use JSON format with previous and current values of all attributes that were changed by this event
         * Required: false
         * Array: false
         */
        this.eventData = undefined;
    }
}
/**
 * Status of a delete expired message request
 */
class DeleteExpiredMessageStatus {
    constructor() {
        /**
         * Unique identifier for the ensemble record.
         * Required: false
         * Array: false
         */
        this.messageId = undefined;
        /**
         * Indicates whether the ensemble was killed.
         * Required: false
         * Array: false
         */
        this.messageDeletedInd = undefined;
        /**
         * A status describing why the message could not be deleted. Populated if messageDeletedInd is false
         * Required: false
         * Array: false
         */
        this.failStatus = undefined;
    }
}
/**
 * Status of a kill ensemble request
 */
class KillEnsembleStatus {
    constructor() {
        /**
         * Unique identifier for the ensemble record.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
        /**
         * Indicates whether the ensemble was killed.
         * Required: false
         * Array: false
         */
        this.ensembleKilledInd = undefined;
        /**
         * A status describing why the ensemble could not be killed. Populated if ensembleKilledInd is false
         * Required: false
         * Array: false
         */
        this.failStatus = undefined;
    }
}
/**
 * Stores execution information for application workflows.
 */
class EnsembleStateSearchRecord {
    constructor() {
        /**
         * Unique identifier for the ensemble record.
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState = undefined;
    }
}
class Payload {
    constructor() {
        /**
         * Required: true
         * Array: false
         */
        this.type = undefined;
        /**
         * Required: true
         * Array: false
         */
        this.value = undefined;
    }
}
class EnsembleState {
    constructor() {
        /**
         * Unique identifier for the ensemble record.
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * Name of the application workflow.
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * A short description of the ensemble
         * Required: false
         * Array: false
         */
        this.desc = undefined;
        /**
         * Name of the queue publishing this message.
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
        /**
         * Id of the message.
         * Required: false
         * Array: false
         */
        this.messageId = undefined;
        /**
         * The timestamp of when the trigger event was created.
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The timestamp of when the message was received by ensemble.
         * Required: false
         * Array: false
         */
        this.recvTmst = undefined;
        /**
         * The timestamp of when this ensemble was dispatched to the executor thread.
         * Required: false
         * Array: false
         */
        this.dispatchTmst = undefined;
        /**
         * The timestamp of when this ensemble processing started.
         * Required: false
         * Array: false
         */
        this.strtTmst = undefined;
        /**
         * The timestamp of when this ensemble processing ended.
         * Required: false
         * Array: false
         */
        this.endTmst = undefined;
        /**
         * Dequeue time in milliseconds. Populated for started ensembles, and is the difference between the received and event timestamps.
         * Required: false
         * Array: false
         */
        this.dequeueTime = undefined;
        /**
         * Dispatch time in milliseconds. Populated for started ensembles, and is the difference between the dispatch and received timestamps.
         * Required: false
         * Array: false
         */
        this.dispatchTime = undefined;
        /**
         * Wait time in milliseconds. Populated for started ensembles, and is the difference between the start and dispatch timestamps.
         * Required: false
         * Array: false
         */
        this.waitTime = undefined;
        /**
         * Time in milliseconds it took to process this ensemble from the time it was started. Populated for completed ensembles, and is the difference between the end and start timestamps.
         * Required: false
         * Array: false
         */
        this.processTime = undefined;
        /**
         * Total elapsed time in milliseconds to process this ensemble from the time the event was created. Populated for completed ensembles, and is the difference between the start and event timestamps.
         * Required: false
         * Array: false
         */
        this.totalTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lstUpdtTmst = undefined;
        /**
         * Status of workflow execution.
         *
         * Valid values:
         * NOT_STARTED
         * STARTED
         * SUSPENDED
         * COMPLETED
         * Required: false
         * Array: false
         */
        this.statCd = undefined;
        /**
         * Status message for the workflow execution.  This could be an error message, stacktrace, success message, etc.
         * Required: false
         * Array: false
         */
        this.statMsg = undefined;
        /**
         * Same value for CORRELATION
         *
         * id is assigned to various rows from different tables that form a single business
         *
         * transactions.
         * In other words, this groups multiple rows across various tables for a single business
         *
         * transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * Name of the host on which the ensemble was last processed
         * Required: false
         * Array: false
         */
        this.host = undefined;
        /**
         * Name of the thread on which the ensemble was last processed
         * Required: false
         * Array: false
         */
        this.thread = undefined;
        /**
         * Timestamp of when the ensemble started waiting for lock
         * Required: false
         * Array: false
         */
        this.lockWaitStartTmst = undefined;
        /**
         * Time spent on waiting for lock
         * Required: false
         * Array: false
         */
        this.elapsedLockWaitTime = undefined;
        /**
         * Number of times this ensemble was put on lock wait
         * Required: false
         * Array: false
         */
        this.lockWaitCount = undefined;
        /**
         * When the ensemble was paused
         * Required: false
         * Array: false
         */
        this.pauseStartTmst = undefined;
        /**
         * When the pause will expire
         * Required: false
         * Array: false
         */
        this.pauseExpiryTime = undefined;
        /**
         * Number of times this ensemble was put on pause
         * Required: false
         * Array: false
         */
        this.pauseCount = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.minuetStates = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.gotoEnsembleStates = undefined;
        /**
         * Payload that is input to the ensemble execution.
         * Required: false
         * Array: false
         */
        this.payload = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.executionContext = undefined;
    }
}
class NestedEnsembleState {
    constructor() {
        /**
         * Name of the application workflow.
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.minuetStates = undefined;
    }
}
class EnsembleExecutionContext {
    constructor() {
        /**
         * Name of the application workflow.
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.minuetPtr = undefined;
    }
}
/**
 * Stores execution information for the tasks that compose the application workflow.
 */
class MinuetState {
    constructor() {
        /**
         * Name of the task.
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Response payload that is output  from the task.
         * Required: false
         * Array: false
         */
        this.respPayload = undefined;
        /**
         * Datetime the task execution started
         * Required: false
         * Array: false
         */
        this.strtTmst = undefined;
        /**
         * Datetime the task execution ended
         * Required: false
         * Array: false
         */
        this.endTmst = undefined;
        /**
         * Time in milliseconds it took to process this minuet from the time it was started. Populated for completed minuets, and is the difference between the end and start timestamps.
         * Required: false
         * Array: false
         */
        this.processTime = undefined;
        /**
         * Number of times this minuet was retried
         * Required: false
         * Array: false
         */
        this.executionCount = undefined;
        /**
         * Status of task execution.
         *
         * Valid values:
         * STARTED
         * FAILED
         * COMPLETED
         * Required: false
         * Array: false
         */
        this.statCd = undefined;
        /**
         * Status message for the task.  This could be an error message, stacktrace, success message, etc.
         * Required: false
         * Array: false
         */
        this.statMsg = undefined;
    }
}
class EnsembleMetrics {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.notStartedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.startedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.completedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.suspendedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.failedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.killedCount = undefined;
    }
}
class PubSubMetrics {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.sentMessageCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ackedMessageCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.expiredAckDeadLinesCount = undefined;
    }
}
class QueueMetrics {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.readyCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.waitCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.processedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.expiredCount = undefined;
    }
}
class AuxMessagePublishers {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.queueTypeCd = undefined;
    }
}
class EnsembleConfiguration {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.component = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.mode = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shortName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.sourceTable = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.payloadType = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.cacheEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.lockEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.retriggerEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.waitEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.rulesEngineEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.messagePublisherEnabled = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.auxMessagePublishers = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileAccessEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.synchronousResponseType = undefined;
    }
}
class Subscription {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.id = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.status = undefined;
    }
}
class Channel {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.status = undefined;
    }
}
class LTLEventEquipmentLicensing {
    constructor() {
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventType = undefined;
        /**
         * Contains a list of additional event types that are included in this event.
         * Required: false
         * Array: true
         */
        this.auxEventTypes = undefined;
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The name of component from which this event originated.
         * Required: false
         * Array: false
         */
        this.srcComponent = undefined;
        /**
         * The correlation ID of the originating transaction
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * A unique id for the event. If this is chunked, the eventId will help in combining chunks of the same message
         * Required: false
         * Array: false
         */
        this.eventId = undefined;
        /**
         * If there are a large number of payloads, the events can be chunked and published as smaller payloads. Set chunkInd to true if this is a chunk of a larger event message
         * Required: false
         * Array: false
         */
        this.chunkedInd = undefined;
        /**
         * If this is a chunked event message, specifies the sequence number of this instance
         * Required: false
         * Array: false
         */
        this.chunkSeqNbr = undefined;
        /**
         * If this is a chunked event message, specifies the total number of chunks
         * Required: false
         * Array: false
         */
        this.totalChunks = undefined;
        /**
         * The event data. Use JSON format with previous and current values of all attributes that were changed by this event
         * Required: false
         * Array: true
         */
        this.eventData = undefined;
    }
}
/**
 * An Update response from a BULK update to elastic
 */
class BulkUpdateResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this._index = undefined;
        /**
         * Required: false
         * Array: false
         */
        this._id = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.result = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.status = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.error = undefined;
    }
}
/**
 * Details of an error for an item
 */
class BulkUpdateItemError {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.type = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.reason = undefined;
    }
}
/**
 * An item in the bulk api response
 */
class BulkApiRespItem {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.update = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.index = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.create = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.delete = undefined;
    }
}
class EsAggregationFieldRqst {
    constructor() {
        /**
         * the name of the property to aggregate on (siteCode, employeeCode, etc.)
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * aggregation type on that property. Options for field object: term, date, daterange, distinctCount. Options for subfield object: term, stats, date, ranges, distinctCount
         * Required: false
         * Array: false
         */
        this.type = undefined;
        /**
         * max number of rows to return
         * Required: false
         * Array: false
         */
        this.size = undefined;
        /**
         * second, minute, hour, day, week, month, quarter, year. Applies only when type is set to date
         * Required: false
         * Array: false
         */
        this.dateInterval = undefined;
        /**
         * min number of documents to filter date interval on. Applies only when type is set to date
         * Required: false
         * Array: false
         */
        this.minimumDocumentCount = undefined;
        /**
         * date range filter
         * Required: false
         * Array: true
         */
        this.ranges = undefined;
        /**
         * min number of documents to filter date interval on. Applies only when type is set to date
         * Required: false
         * Array: true
         */
        this.subFields = undefined;
    }
}
class EsAggregationDateRange {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.from = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.to = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.key = undefined;
    }
}
class EsDynamicPivotRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
    }
}
class EsAggregationRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.dynamicPivot = undefined;
    }
}
class EsAggregationFieldResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.key = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.date = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.from = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fromAsString = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.to = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.toAsString = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.docCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.subFields = undefined;
    }
}
class EsAggregationResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.dynamicPivot = undefined;
    }
}
class EnsembleStateSearchFilterNot {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.and = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.or = undefined;
    }
}
class EnsembleStateSearchFilterOr {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.instId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host_keyword = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.and = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.not = undefined;
        this.toJSON = function () {
            return {
                "instId": this.instId,
                "instId.keyword": this.instId_keyword,
                "ensembleState.messageId": this.ensembleState_messageId,
                "ensembleState.messageId.keyword": this.ensembleState_messageId_keyword,
                "ensembleState.queueName": this.ensembleState_queueName,
                "ensembleState.queueName.keyword": this.ensembleState_queueName_keyword,
                "ensembleState.name": this.ensembleState_name,
                "ensembleState.name.keyword": this.ensembleState_name_keyword,
                "ensembleState.desc": this.ensembleState_desc,
                "ensembleState.desc.keyword": this.ensembleState_desc_keyword,
                "ensembleState.eventTmst": this.ensembleState_eventTmst,
                "ensembleState.eventTmst.keyword": this.ensembleState_eventTmst_keyword,
                "ensembleState.strtTmst": this.ensembleState_strtTmst,
                "ensembleState.strtTmst.keyword": this.ensembleState_strtTmst_keyword,
                "ensembleState.endTmst": this.ensembleState_endTmst,
                "ensembleState.endTmst.keyword": this.ensembleState_endTmst_keyword,
                "ensembleState.statCd": this.ensembleState_statCd,
                "ensembleState.statCd.keyword": this.ensembleState_statCd_keyword,
                "ensembleState.correlationId": this.ensembleState_correlationId,
                "ensembleState.correlationId.keyword": this.ensembleState_correlationId_keyword,
                "ensembleState.businessKey1": this.ensembleState_businessKey1,
                "ensembleState.businessKey1.keyword": this.ensembleState_businessKey1_keyword,
                "ensembleState.businessKey2": this.ensembleState_businessKey2,
                "ensembleState.businessKey2.keyword": this.ensembleState_businessKey2_keyword,
                "ensembleState.businessKey3": this.ensembleState_businessKey3,
                "ensembleState.businessKey3.keyword": this.ensembleState_businessKey3_keyword,
                "ensembleState.businessKey4": this.ensembleState_businessKey4,
                "ensembleState.businessKey4.keyword": this.ensembleState_businessKey4_keyword,
                "ensembleState.businessKey5": this.ensembleState_businessKey5,
                "ensembleState.businessKey5.keyword": this.ensembleState_businessKey5_keyword,
                "ensembleState.host": this.ensembleState_host,
                "ensembleState.host.keyword": this.ensembleState_host_keyword,
                "and": this.and,
                "not": this.not
            };
        };
    }
}
class EnsembleStateSearchFilterAnd {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.instId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host_keyword = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.or = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.not = undefined;
        this.toJSON = function () {
            return {
                "instId": this.instId,
                "instId.keyword": this.instId_keyword,
                "ensembleState.messageId": this.ensembleState_messageId,
                "ensembleState.messageId.keyword": this.ensembleState_messageId_keyword,
                "ensembleState.queueName": this.ensembleState_queueName,
                "ensembleState.queueName.keyword": this.ensembleState_queueName_keyword,
                "ensembleState.name": this.ensembleState_name,
                "ensembleState.name.keyword": this.ensembleState_name_keyword,
                "ensembleState.desc": this.ensembleState_desc,
                "ensembleState.desc.keyword": this.ensembleState_desc_keyword,
                "ensembleState.eventTmst": this.ensembleState_eventTmst,
                "ensembleState.eventTmst.keyword": this.ensembleState_eventTmst_keyword,
                "ensembleState.strtTmst": this.ensembleState_strtTmst,
                "ensembleState.strtTmst.keyword": this.ensembleState_strtTmst_keyword,
                "ensembleState.endTmst": this.ensembleState_endTmst,
                "ensembleState.endTmst.keyword": this.ensembleState_endTmst_keyword,
                "ensembleState.statCd": this.ensembleState_statCd,
                "ensembleState.statCd.keyword": this.ensembleState_statCd_keyword,
                "ensembleState.correlationId": this.ensembleState_correlationId,
                "ensembleState.correlationId.keyword": this.ensembleState_correlationId_keyword,
                "ensembleState.businessKey1": this.ensembleState_businessKey1,
                "ensembleState.businessKey1.keyword": this.ensembleState_businessKey1_keyword,
                "ensembleState.businessKey2": this.ensembleState_businessKey2,
                "ensembleState.businessKey2.keyword": this.ensembleState_businessKey2_keyword,
                "ensembleState.businessKey3": this.ensembleState_businessKey3,
                "ensembleState.businessKey3.keyword": this.ensembleState_businessKey3_keyword,
                "ensembleState.businessKey4": this.ensembleState_businessKey4,
                "ensembleState.businessKey4.keyword": this.ensembleState_businessKey4_keyword,
                "ensembleState.businessKey5": this.ensembleState_businessKey5,
                "ensembleState.businessKey5.keyword": this.ensembleState_businessKey5_keyword,
                "ensembleState.host": this.ensembleState_host,
                "ensembleState.host.keyword": this.ensembleState_host_keyword,
                "or": this.or,
                "not": this.not
            };
        };
    }
}
class EnsembleStateSearchFilter {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.q = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.b = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.instId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host_keyword = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.and = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.or = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.not = undefined;
        this.toJSON = function () {
            return {
                "q": this.q,
                "b": this.b,
                "instId": this.instId,
                "instId.keyword": this.instId_keyword,
                "ensembleState.messageId": this.ensembleState_messageId,
                "ensembleState.messageId.keyword": this.ensembleState_messageId_keyword,
                "ensembleState.queueName": this.ensembleState_queueName,
                "ensembleState.queueName.keyword": this.ensembleState_queueName_keyword,
                "ensembleState.name": this.ensembleState_name,
                "ensembleState.name.keyword": this.ensembleState_name_keyword,
                "ensembleState.desc": this.ensembleState_desc,
                "ensembleState.desc.keyword": this.ensembleState_desc_keyword,
                "ensembleState.eventTmst": this.ensembleState_eventTmst,
                "ensembleState.eventTmst.keyword": this.ensembleState_eventTmst_keyword,
                "ensembleState.strtTmst": this.ensembleState_strtTmst,
                "ensembleState.strtTmst.keyword": this.ensembleState_strtTmst_keyword,
                "ensembleState.endTmst": this.ensembleState_endTmst,
                "ensembleState.endTmst.keyword": this.ensembleState_endTmst_keyword,
                "ensembleState.statCd": this.ensembleState_statCd,
                "ensembleState.statCd.keyword": this.ensembleState_statCd_keyword,
                "ensembleState.correlationId": this.ensembleState_correlationId,
                "ensembleState.correlationId.keyword": this.ensembleState_correlationId_keyword,
                "ensembleState.businessKey1": this.ensembleState_businessKey1,
                "ensembleState.businessKey1.keyword": this.ensembleState_businessKey1_keyword,
                "ensembleState.businessKey2": this.ensembleState_businessKey2,
                "ensembleState.businessKey2.keyword": this.ensembleState_businessKey2_keyword,
                "ensembleState.businessKey3": this.ensembleState_businessKey3,
                "ensembleState.businessKey3.keyword": this.ensembleState_businessKey3_keyword,
                "ensembleState.businessKey4": this.ensembleState_businessKey4,
                "ensembleState.businessKey4.keyword": this.ensembleState_businessKey4_keyword,
                "ensembleState.businessKey5": this.ensembleState_businessKey5,
                "ensembleState.businessKey5.keyword": this.ensembleState_businessKey5_keyword,
                "ensembleState.host": this.ensembleState_host,
                "ensembleState.host.keyword": this.ensembleState_host_keyword,
                "and": this.and,
                "or": this.or,
                "not": this.not
            };
        };
    }
}
/**
 * Path parameters
 */
class GetElasticDocumentEnsembleStatePath {
    constructor() {
        /**
         * Uniquely identify the elastic search document
         * Required: true
         * Array: false
         */
        this.instId = undefined;
    }
}
/**
 * Query parameters
 */
class GetElasticDocumentEnsembleStateQuery {
    constructor() {
        /**
         * Source filter
         * Required: false
         * Array: false
         */
        this._source = undefined;
        /**
         * Source include filter
         * Required: false
         * Array: false
         */
        this._source_includes = undefined;
        /**
         * Source exclude filter
         * Required: false
         * Array: false
         */
        this._source_excludes = undefined;
    }
}
/**
 * Response from getElasticDocument.
 */
class GetElasticDocumentEnsembleStateResp {
    constructor() {
        /**
         * The record if found
         * Required: false
         * Array: false
         */
        this.record = undefined;
    }
}
/**
 * Query parameters
 */
class GetElasticDocumentEnsembleStateCountQuery {
    constructor() {
        /**
         * Query filter
         * Required: false
         * Array: false
         */
        this.q = undefined;
    }
}
/**
 * Response from getElasticDocumentCount.
 */
class GetElasticDocumentEnsembleStateCountResp {
    constructor() {
        /**
         * The count of the documents
         * Required: false
         * Array: false
         */
        this.count = undefined;
    }
}
/**
 * Request message to update information for EnsembleState.
 */
class UpdateElasticDocumentEnsembleStateRqst {
    constructor() {
        /**
         * The record to upsert in elastic
         * Required: false
         * Array: false
         */
        this.doc = undefined;
        /**
         * Indicate if an upsert operation should be executed if the document was not found
         * Required: false
         * Array: false
         */
        this.doc_as_upsert = undefined;
    }
}
/**
 * Path parameters
 */
class UpdateElasticDocumentEnsembleStatePath {
    constructor() {
        /**
         * Uniquely identify the elastic search document
         * Required: true
         * Array: false
         */
        this.instId = undefined;
    }
}
/**
 * Response from updateElasticDocument.
 */
class UpdateElasticDocumentEnsembleStateResp {
    constructor() {
        /**
         * The result of the operation
         * Required: false
         * Array: false
         */
        this.result = undefined;
    }
}
/**
 * Response from bulkUpdateElasticDocument.
 */
class BulkUpdateElasticDocumentEnsembleStateResp {
    constructor() {
        /**
         * The time it took to process this request
         * Required: false
         * Array: false
         */
        this.took = undefined;
        /**
         * Indicates if there are any errors
         * Required: false
         * Array: false
         */
        this.errors = undefined;
        /**
         * Results of the bulk operations
         * Required: false
         * Array: true
         */
        this.items = undefined;
    }
}
/**
 * Path parameters
 */
class DeleteElasticDocumentEnsembleStatePath {
    constructor() {
        /**
         * Uniquely identify the elastic search document
         * Required: true
         * Array: false
         */
        this.instId = undefined;
    }
}
/**
 * Request message to delete information for EnsembleState.
 */
class DeleteElasticDocumentEnsembleStateResp {
    constructor() {
        /**
         * The result of the operation
         * Required: false
         * Array: false
         */
        this.result = undefined;
    }
}
/**
 * Request message to search information for ensemble-state-unfiltered.
 */
class SearchEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for ensemble-state-unfiltered.
 */
class SearchEnsembleStateUnfilteredResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for ensemble-state-unfiltered.
 */
class ExportEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterEnsembleStateUnfilteredResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsEnsembleStateUnfilteredResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
/**
 * The validation error message that is associated with the identifiers for the equipment.
 */
class EquipmentValidationError {
    constructor() {
        /**
         * The number that is associated with the piece of equipment.
         * Required: false
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         * The sequence number of the equipment.
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         * The error message that is related to the failed equipment.
         * Required: false
         * Array: false
         */
        this.errorMessage = undefined;
    }
}
/**
 * An invalid license code and its associated error message.
 */
class LicenseCodeError {
    constructor() {
        /**
         * The unique identifier for a license.
         * Required: false
         * Array: false
         */
        this.licenseCd = undefined;
        /**
         * The error message that is related to the license.
         * Required: false
         * Array: false
         */
        this.errorMessage = undefined;
    }
}
/**
 * The payload which is used for LIC maintenance via the API channel.
 */
class LicMaintPayload {
    constructor() {
        /**
         * The code which determines whether to update or delete the entities.
         * Required: false
         * Array: false
         */
        this.actionCd = undefined;
        /**
         * The list of unique identifiers for the Equipment that will be updated.
         * Required: false
         * Array: true
         */
        this.equipmentNbrs = undefined;
        /**
         * The Equipment details which will be used as a model for the bulk update.
         * Required: false
         * Array: false
         */
        this.equipment = undefined;
        /**
         * The comment which will be added in all update references.
         * Required: false
         * Array: false
         */
        this.comment = undefined;
        /**
         * The list of unique identifiers for the License Codes that will be updated or deleted.
         * Required: false
         * Array: true
         */
        this.licenseCds = undefined;
        /**
         * The License Code details which will be used to perform the update.
         * Required: false
         * Array: false
         */
        this.licenseCode = undefined;
        /**
         * The standard audit trail information.
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * The list of License Code Requirements to be deleted from the License Code.
         * Required: false
         * Array: true
         */
        this.licenseCodeRequirementDeletions = undefined;
        /**
         * The list of License Code State Operations to be deleted from the License Code.
         * Required: false
         * Array: true
         */
        this.licenseCodeStateOperationsDeletions = undefined;
        /**
         * The list of License Code States to be deleted from the License Code.
         * Required: false
         * Array: true
         */
        this.licenseCodeStateDeletions = undefined;
    }
}
/**
 * The details of the old and new state weight groups with a comparison indicator.
 */
class StateWeightGroupComparison {
    constructor() {
        /**
         * The name of the state.
         * Required: false
         * Array: false
         */
        this.stateName = undefined;
        /**
         * The code associated with the state.
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         * The code associated with the old weight group.
         * Required: false
         * Array: false
         */
        this.oldWeightGroupCd = undefined;
        /**
         * The weight, in pounds, of the old weight group for the state.
         * Required: false
         * Array: false
         */
        this.oldWeightLbs = undefined;
        /**
         * The code associated with the new weight group.
         * Required: false
         * Array: false
         */
        this.newWeightGroupCd = undefined;
        /**
         * The weight, in pounds, of the new weight group for the state.
         * Required: false
         * Array: false
         */
        this.newWeightLbs = undefined;
        /**
         * The flag which indicates whether the weight has changed between the old and new weight groups.
         * Required: false
         * Array: false
         */
        this.weightChangedInd = undefined;
    }
}
/**
 * The list of grid header details for State Weight Group.
 */
class StateWeightGroupHeader {
    constructor() {
        /**
         * The field for the column that is going to be displayed in the grid.
         * Required: false
         * Array: false
         */
        this.field = undefined;
        /**
         * The name of the column header that is going to be displayed in the grid.
         * Required: false
         * Array: false
         */
        this.headerName = undefined;
    }
}
/**
 * Store the information of Corporations by using a Code & Description
 */
class Corp {
    constructor() {
        /**
         * XPO divisions
         * Example:
         * CCX
         * XPOD
         * XPOEX
         * XPOLM
         * XPOMT
         * Required: false
         * Array: false
         */
        this.corpCd = undefined;
        /**
         * XPO or Legacy companies e.g CNW for Con-way
         * Required: false
         * Array: false
         */
        this.companyCd = undefined;
        /**
         * Name of the Corp
         * Required: false
         * Array: false
         */
        this.corpName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * This table is to maintain all the details of  Task/Project related to a particular Requirement.  Added 05/31/2022  LISG-16820
 */
class CorpCodeRequirementProject {
    constructor() {
        /**
         * Uniquely identifies a project(task) with a corp code related to a specific requirement.
         * Required: false
         * Array: false
         */
        this.corpCodeRequirementProjectId = undefined;
        /**
         * Date entered manually by the user when the application is filed for a requirement(example: application for new permit)
         * Required: false
         * Array: false
         */
        this.applicationDate = undefined;
        /**
         * Date entered manually by the user when the application is received(example: receive new/updated permit)
         * Required: false
         * Array: false
         */
        this.receivedDate = undefined;
        /**
         * Date when all the steps of the application are completed(Example: The new permit is sent out)
         * Required: false
         * Array: false
         */
        this.completedDate = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * This will record the username of the person who had filled up the details required for the project.Added on 08/28/23 LISG-54854
         * Required: false
         * Array: false
         */
        this.enteredByUser = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Uniquely identifies a requirement project (FK from LIC_REQUIREMENT_PROJECT)
         * Required: false
         * Array: false
         */
        this.parentRequirementProjectId = undefined;
    }
}
/**
 *
 */
class CorpCodeRequirementProjectHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.corpCodeRequirementProjectId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.requirementProjectId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.applicationDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.receivedDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.completedDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
    }
}
/**
 * Store equipment details on Licensing App.
 */
class Equipment {
    constructor() {
        /**
         * Identifies an equipment
         * Required: false
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         * SIC code that is charged for this equipment
         * Required: false
         * Array: false
         */
        this.chargeSicCd = undefined;
        /**
         * Location where this equipment is intalled/operated
         * Required: false
         * Array: false
         */
        this.installSicCd = undefined;
        /**
         * State that issued the license
         * Required: false
         * Array: false
         */
        this.licenseStateCd = undefined;
        /**
         * License plate number
         * Required: false
         * Array: false
         */
        this.licensePlateNbr = undefined;
        /**
         * Type of plate: P for power or T for trailer
         * Required: false
         * Array: false
         */
        this.plateTypeCd = undefined;
        /**
         * Year and month when the License expire
         * Required: false
         * Array: false
         */
        this.expireDateYyyymm = undefined;
        /**
         * Gross weight of freight plus equipment.  10/03/2023 changed from NUMBER to VARCHAR and added TXT suffix LISG-55104
         * Required: false
         * Array: false
         */
        this.grossWeightLbs = undefined;
        /**
         * Weight used in tax calculation.  10/03/2023 changed from NUMBER to VARCHAR and added TXT suffix LISG-55104
         * Required: false
         * Array: false
         */
        this.taxWeightLbs = undefined;
        /**
         * year when equipment was Manufactured
         * Required: false
         * Array: false
         */
        this.equipmentYrYyyy = undefined;
        /**
         * Manufacturer Make
         * Required: false
         * Array: false
         */
        this.equipmentMake = undefined;
        /**
         * Manufacturer Serial Number for the equipment
         * Required: false
         * Array: false
         */
        this.vinNbr = undefined;
        /**
         * Type of Fuel:  GAS, DIESEL
         * Required: false
         * Array: false
         */
        this.fuelTypeCd = undefined;
        /**
         * Number of Axle on the equipment.  10/03/2023 changed from NUMBER to VARCHAR and added TXT suffix LISG-55104
         * Required: false
         * Array: false
         */
        this.axlesCount = undefined;
        /**
         * BD         BOMM DOLLY
         * CR          CRANE
         * CT          CONTAINER
         * DB         DROM BOX
         * DL          CONVERTER DOLLY
         * DP         DUMP TRUCK
         * FB          FLAT BED
         * GR         GRADER
         * HP        HOPPER
         * JP           JEEP AXLE
         * LB          LOW BED
         * LT          LIFT TRUCK
         * OV         OPEN TOP VAN
         * PA          PANEL
         * PC          PASSENGER CAR
         * PU         PICKUP
         * RD         RADIO
         * RF          REFRIDGERATION VAN
         * RU         REEFER UNIT
         * SD          SEDAN
         * SM         SHOP MULE
         * SP          SNOW PLOW
         * ST          SEMI TRAILER
         * SW        YARD SWEEPER
         * TA          TANK
         * TK          TRUCK
         * TL          FULL TRAILER
         * TR          TRACTOR
         * TT          TRUCK TRACTOR
         * VN         DRY VAN
         * Required: false
         * Array: false
         */
        this.bodyCd = undefined;
        /**
         * CONV
         * GRDR
         * LIFT
         * MOWR
         * MULE
         * PASS
         * SEMI
         * SPLW
         * SWPR
         * TRAC
         * TRLR
         * TRUK
         * TYPE
         * WRKR
         * Required: false
         * Array: false
         */
        this.equipmentTypeCd = undefined;
        /**
         * Weight of equipment without weight.  10/03/2023 changed from NUMBER to VARCHAR and added TXT suffix LISG-55104
         * Required: false
         * Array: false
         */
        this.emptyWeightLbs = undefined;
        /**
         * Cost of the equipment.  10/03/2023 changed from NUMBER to VARCHAR and added TXT suffix LISG-55104
         * Required: false
         * Array: false
         */
        this.vehicleCostAmount = undefined;
        /**
         * Original cost.  10/03/2023 changed from NUMBER to VARCHAR and added TXT suffix LISG-55104
         * Required: false
         * Array: false
         */
        this.factoryCostAmount = undefined;
        /**
         * Year and month when the equipment was purchased 12/02/2019 - switched to Type DATE instead of VARCHAR2(6)
         * Required: false
         * Array: false
         */
        this.purchaseDate = undefined;
        /**
         * Date when the equipment was sold by XPO
         * Required: false
         * Array: false
         */
        this.sellDate = undefined;
        /**
         * Used for storing the previous equipment number when the equipment changes its purpose
         * Required: false
         * Array: false
         */
        this.priorEquipmentNbr = undefined;
        /**
         * State that issued the title
         * Required: false
         * Array: false
         */
        this.titleStateCd = undefined;
        /**
         * Title Number
         * Required: false
         * Array: false
         */
        this.titleNbr = undefined;
        /**
         * Year when the equipment was received
         * Required: false
         * Array: false
         */
        this.equipmentReceiveYrYyyy = undefined;
        /**
         * Year when equipment retires
         * Required: false
         * Array: false
         */
        this.retireYrYyyy = undefined;
        /**
         * Whether the equipment is Owned or Leased
         * Required: false
         * Array: false
         */
        this.ownedLeaseCd = undefined;
        /**
         * The first 2 digits are the state the fleet is registered out of and the last digit is the year of the fleet registration.  For example most active vehicles right now show IN9 which tells us the vehicle is part of the IN based fleet for 2019.
         * Required: false
         * Array: false
         */
        this.apportionedCode = undefined;
        /**
         * Information about the lessor, if the XPO is leasing the equipment
         * Required: false
         * Array: false
         */
        this.lessorInformation = undefined;
        /**
         * New York Tax related information
         * Required: false
         * Array: false
         */
        this.newyorkTaxSeries = undefined;
        /**
         * Manufacturer Model
         * Required: false
         * Array: false
         */
        this.equipmentModelNbr = undefined;
        /**
         * Identifies what equipment was replaced by this equipment
         * Required: false
         * Array: false
         */
        this.retiredUnit = undefined;
        /**
         * Identifies what equipment replaced the previous equipment.
         * Required: false
         * Array: false
         */
        this.replacedBy = undefined;
        /**
         * State where equipment resides
         * Required: false
         * Array: false
         */
        this.domicileStateCd = undefined;
        /**
         * Indicates the status when the title of the equipment is sent out  to Service Center/DMV for updates/changes.
         * Required: false
         * Array: false
         */
        this.titleOutInd = undefined;
        /**
         * Heavy Vehical Use Tax Amount.  10/03/2023 changed from NUMBER to VARCHAR and added TXT suffix LISG-55104
         * Required: false
         * Array: false
         */
        this.hvutTaxAmount = undefined;
        /**
         * Year and month of Heavy Vehical Use Tax
         * Required: false
         * Array: false
         */
        this.hvutTaxDateYyyymm = undefined;
        /**
         * State where sales tax was paid
         * Required: false
         * Array: false
         */
        this.salesTaxStateCd = undefined;
        /**
         * Month and year when Sales tax was paid
         * Required: false
         * Array: false
         */
        this.salesTaxDateYyyymm = undefined;
        /**
         * Comment related to Sales tax payment
         * Required: false
         * Array: false
         */
        this.salesTaxComment = undefined;
        /**
         * Sales Tax Amount.  10/03/2023 changed from NUMBER to VARCHAR and added TXT suffix LISG-55104
         * Required: false
         * Array: false
         */
        this.salesTaxAmount = undefined;
        /**
         * Previous Plate Number on the equipment
         * Required: false
         * Array: false
         */
        this.prevPlateNbr = undefined;
        /**
         * Each state allows certain weight group.  10/30/203 LISG-55324 increased length from 3 to 15
         * Required: false
         * Array: false
         */
        this.weightGroupCd = undefined;
        /**
         * EZ Pass (toll collection system)
         * Required: false
         * Array: false
         */
        this.ezPass = undefined;
        /**
         * New York Highway Use Tax Nbr
         * Required: false
         * Array: false
         */
        this.nyHwyUseTaxNbr = undefined;
        /**
         * US Transponder Number
         * Required: false
         * Array: false
         */
        this.usTransponderNbr = undefined;
        /**
         * Vehicle Reference code - it is used in tolling
         * Required: false
         * Array: false
         */
        this.vehicleRefCd = undefined;
        /**
         * PrePass (Is weigh station bypass and tolling device)
         * Required: false
         * Array: false
         */
        this.weightStnPrepass = undefined;
        /**
         * NORPASS(weigh station bypass system)
         * Required: false
         * Array: false
         */
        this.weightStnNorpass = undefined;
        /**
         * FL Sunpass (is the tolling transponder number issued by Sunpass)
         * Required: false
         * Array: false
         */
        this.flSunpass = undefined;
        /**
         * Texas Department of Transportation TxTag ( this is the tolling device number issued by tolling authority TXTag)
         * Required: false
         * Array: false
         */
        this.txTag = undefined;
        /**
         * Harris County Toll Road Authority (this is the tolling device number issued by Harris Countin Toll Road Authority)
         * Required: false
         * Array: false
         */
        this.harrisCntyTollAuth = undefined;
        /**
         * PikePass (This is the tolling device number issued by 'Pikepass' toll system)
         * Required: false
         * Array: false
         */
        this.pikepassToll = undefined;
        /**
         * This is the 6 digit profit center number for a location in Oracle.  12/13/2019 increased length from 3 to 6, and removed SIC from name
         * Required: false
         * Array: false
         */
        this.profitCenterCd = undefined;
        /**
         * It allows us to identify a subset of particular vehicles that dont fit into any group we can do an inquiry on.  For example if LTL sends  a list of 150 various tractors they want to sell. A unique identifier is assiigned as the subset cd in this field (say 1234) in order to pull a list of those specific 100 tractors with any info.
         * Required: false
         * Array: false
         */
        this.subsetId = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Date when the equipment was put in service by XPO.  Added 03/23/2022 LISG-53456
         * Required: false
         * Array: false
         */
        this.inServiceDate = undefined;
        /**
         * In current application, this is a single digit number corresponding to the last number of the year the vehicle is originally planned for purchase (sometimes vehicles get delayed and pushed into following year).  We just use the single digit based on last digit of the year it is purchased in.  After we have completed a year of purchases. That field is cleared out.   Added 05/08/2023  LISG-53857 This is NewCode in tblLicense table in existing system
         * Required: false
         * Array: false
         */
        this.planPurchaseYearYyyy = undefined;
        /**
         * In current application, this is a single digit number corresponding with the last number of the year (for example '3' is for 2023).  We only use this column when we are told a unit is 'scheduled to retire' against a new tractor.  It is never used other than that so that is why it is just a single digit.  For example in 2023 LTL might provide me a list of 300 tractors that will be retired against new tractors coming on.  I will put a '3' (for 202'3') in the TR column so that my team knows that unit will be retiring soon.  That way we might not need to purchase a $1200 permit for triples etc.  At the end of 2023 we clear out any '3s' in the TR column as its no longer needed.  Added 05/08/2023  LISG-53857 This is TransferCode in tblLicense table in existing system
         * Required: false
         * Array: false
         */
        this.scheduledRetireYearYyyy = undefined;
        /**
         * Original name of field was 'Operation code'.  This code was originally used to reference a primary location various smaller locations ran out of and were serviced by that particular shop.  For example.. Portland, Eugene, Medford might all be serviced out of Portland shop and be considered part of Portland Operations so they would show an OPS code of UPO.  Added 05/08/2023  LISG-53857 This is Operation Code in tblLicense table in existing system
         * Required: false
         * Array: false
         */
        this.servicedBySicCd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.parentLicenseCd = undefined;
        /**
         * Foreign key from Fleet Code.  10/03/2023 changed from NUMBER to VARCHAR and added the actual FK constraint LISG-55104
         * Required: false
         * Array: false
         */
        this.parentFleetCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.parentCorpCd = undefined;
        /**
         * The sequence number of the equipment.
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
    }
}
/**
 * Store comments regarding equipment and also further replies to the comments, thus building chain of comments
 */
class EquipmentComment {
    constructor() {
        /**
         * Equipment for
         *
         * which comment is entered
         * Required: false
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         * For a given equipment,
         *
         * this uniquely identifies a comment
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         * Comment text
         * Required: false
         * Array: false
         */
        this.comment = undefined;
        /**
         * This allows
         *
         * us to link thei comment to the previous comment of this conversation/thread for a
         *
         * given equipment.
         * 	Thus multiple threads /conversations of comments can be stored.
         * This identifies the comment for which this comment is the reply.
         * Note, the previous comment is assumed to be for the same equipment as this comment.
         * Required: false
         * Array: false
         */
        this.prevCommentSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Employee details for the create by employee id.
         * Required: false
         * Array: false
         */
        this.createByEmployeeDtl = undefined;
    }
}
/**
 * Stores one or more Equipment detail data rows uploaded within a single CSV File Upload. This table holds Equipment rows uploaded within a single CSV File Upload.  Added 02/03/2020
 */
class EquipmentCsv {
    constructor() {
        /**
         * Unique ID assigned by system to a CSV File Upload when the upload occurs and the metadata row is created.
         * Required: false
         * Array: false
         */
        this.uploadId = undefined;
        /**
         * Identifies an equipment
         * Required: false
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         * SIC code that is charged for this equipment
         * Required: false
         * Array: false
         */
        this.chargeSicCd = undefined;
        /**
         * Location where this equipment is intalled/operated
         * Required: false
         * Array: false
         */
        this.installSicCd = undefined;
        /**
         * Current License number for this equipment
         * Required: false
         * Array: false
         */
        this.licenseCd = undefined;
        /**
         * State that issued the license
         * Required: false
         * Array: false
         */
        this.licenseStateCd = undefined;
        /**
         * License plate number
         * Required: false
         * Array: false
         */
        this.licensePlateNbr = undefined;
        /**
         * Type of plate: P for power or T for trailer
         * Required: false
         * Array: false
         */
        this.plateTypeCd = undefined;
        /**
         * Year and month when the License expire
         * Required: false
         * Array: false
         */
        this.expireDateYyyymm = undefined;
        /**
         * Foreign key from Fleet Code
         * Required: false
         * Array: false
         */
        this.fleetCodeId = undefined;
        /**
         * Gross weight of freight plus equipment
         * Required: false
         * Array: false
         */
        this.grossWeightLbs = undefined;
        /**
         * Weight used in tax calculation
         * Required: false
         * Array: false
         */
        this.taxWeightLbs = undefined;
        /**
         * year when equipment was Manufactured
         * Required: false
         * Array: false
         */
        this.equipmentYrYyyy = undefined;
        /**
         * Manufacturer Make
         * Required: false
         * Array: false
         */
        this.equipmentMake = undefined;
        /**
         * Manufacturer Serial Number for the equipment
         * Required: false
         * Array: false
         */
        this.vinNbr = undefined;
        /**
         * Type of Fuel:  GAS, DIESEL
         * Required: false
         * Array: false
         */
        this.fuelTypeCd = undefined;
        /**
         * Number of Axle on the equipment
         * Required: false
         * Array: false
         */
        this.axlesCount = undefined;
        /**
         * BD         BOMM DOLLY
         * CR          CRANE
         * CT          CONTAINER
         * DB         DROM BOX
         * DL          CONVERTER DOLLY
         * DP         DUMP TRUCK
         * FB          FLAT BED
         * GR         GRADER
         * HP        HOPPER
         * JP           JEEP AXLE
         * LB          LOW BED
         * LT          LIFT TRUCK
         * OV         OPEN TOP VAN
         * PA          PANEL
         * PC          PASSENGER CAR
         * PU         PICKUP
         * RD         RADIO
         * RF          REFRIDGERATION VAN
         * RU         REEFER UNIT
         * SD          SEDAN
         * SM         SHOP MULE
         * SP          SNOW PLOW
         * ST          SEMI TRAILER
         * SW        YARD SWEEPER
         * TA          TANK
         * TK          TRUCK
         * TL          FULL TRAILER
         * TR          TRACTOR
         * TT          TRUCK TRACTOR
         * VN         DRY VAN
         * Required: false
         * Array: false
         */
        this.bodyCd = undefined;
        /**
         * CONV
         * GRDR
         * LIFT
         * MOWR
         * MULE
         * PASS
         * SEMI
         * SPLW
         * SWPR
         * TRAC
         * TRLR
         * TRUK
         * TYPE
         * WRKR
         * Required: false
         * Array: false
         */
        this.equipmentTypeCd = undefined;
        /**
         * Weight of equipment without weight
         * Required: false
         * Array: false
         */
        this.emptyWeightLbs = undefined;
        /**
         * Cost of the equipment
         * Required: false
         * Array: false
         */
        this.vehicleCostAmount = undefined;
        /**
         * Original cost
         * Required: false
         * Array: false
         */
        this.factoryCostAmount = undefined;
        /**
         * Year and month when the equipment was purchased 12/02/2019 - switched to Type DATE instead of VARCHAR2(6)
         * Required: false
         * Array: false
         */
        this.purchaseDate = undefined;
        /**
         * Date when the equipment was sold by XPO
         * Required: false
         * Array: false
         */
        this.sellDate = undefined;
        /**
         * Used for storing the previous equipment number when the equipment changes its purpose
         * Required: false
         * Array: false
         */
        this.priorEquipmentNbr = undefined;
        /**
         * State that issued the title
         * Required: false
         * Array: false
         */
        this.titleStateCd = undefined;
        /**
         * Title Number
         * Required: false
         * Array: false
         */
        this.titleNbr = undefined;
        /**
         * Year when the equipment was received
         * Required: false
         * Array: false
         */
        this.equipmentReceiveYrYyyy = undefined;
        /**
         * Year when equipment retires
         * Required: false
         * Array: false
         */
        this.retireYrYyyy = undefined;
        /**
         * Whether the equipment is Owned or Leased
         * Required: false
         * Array: false
         */
        this.ownedLeaseCd = undefined;
        /**
         * The first 2 digits are the state the fleet is registered out of and the last digit is the year of the fleet registration.  For example most active vehicles right now show IN9 which tells us the vehicle is part of the IN based fleet for 2019.
         * Required: false
         * Array: false
         */
        this.apportionedCode = undefined;
        /**
         * Information about the lessor, if the XPO is leasing the equipment
         * Required: false
         * Array: false
         */
        this.lessorInformation = undefined;
        /**
         * New York Tax related information
         * Required: false
         * Array: false
         */
        this.newyorkTaxSeries = undefined;
        /**
         * Manufacturer Model
         * Required: false
         * Array: false
         */
        this.equipmentModelNbr = undefined;
        /**
         * Identifies what equipment was replaced by this equipment
         * Required: false
         * Array: false
         */
        this.retiredUnit = undefined;
        /**
         * Identifies what equipment replaced the previous equipment.
         * Required: false
         * Array: false
         */
        this.replacedBy = undefined;
        /**
         * Corp Code e.g. XPOMT, XPOSC, CNW (legacy Con-way)
         * Required: false
         * Array: false
         */
        this.corpCd = undefined;
        /**
         * State where equipment resides
         * Required: false
         * Array: false
         */
        this.domicileStateCd = undefined;
        /**
         * Indicates the status when the title of the equipment is sent out  to Service Center/DMV for updates/changes.
         * Required: false
         * Array: false
         */
        this.titleOutInd = undefined;
        /**
         * Heavy Vehical Use Tax Amount
         * Required: false
         * Array: false
         */
        this.hvutTaxAmount = undefined;
        /**
         * Year and month of Heavy Vehical Use Tax
         * Required: false
         * Array: false
         */
        this.hvutTaxDateYyyymm = undefined;
        /**
         * State where sales tax was paid
         * Required: false
         * Array: false
         */
        this.salesTaxStateCd = undefined;
        /**
         * Month and year when Sales tax was paid
         * Required: false
         * Array: false
         */
        this.salesTaxDateYyyymm = undefined;
        /**
         * Comment related to Sales tax payment
         * Required: false
         * Array: false
         */
        this.salesTaxComment = undefined;
        /**
         * Sales Tax Amount
         * Required: false
         * Array: false
         */
        this.salesTaxAmount = undefined;
        /**
         * Previous Plate Number on the equipment
         * Required: false
         * Array: false
         */
        this.prevPlateNbr = undefined;
        /**
         * Each state allows certain weight group
         * Required: false
         * Array: false
         */
        this.weightGroupCd = undefined;
        /**
         * EZ Pass (toll collection system)
         * Required: false
         * Array: false
         */
        this.ezPass = undefined;
        /**
         * New York Highway Use Tax Nbr
         * Required: false
         * Array: false
         */
        this.nyHwyUseTaxNbr = undefined;
        /**
         * US Transponder Number
         * Required: false
         * Array: false
         */
        this.usTransponderNbr = undefined;
        /**
         * Vehicle Reference code - it is used in tolling
         * Required: false
         * Array: false
         */
        this.vehicleRefCd = undefined;
        /**
         * PrePass (Is weigh station bypass and tolling device)
         * Required: false
         * Array: false
         */
        this.weightStnPrepass = undefined;
        /**
         * NORPASS(weigh station bypass system)
         * Required: false
         * Array: false
         */
        this.weightStnNorpass = undefined;
        /**
         * FL Sunpass (is the tolling transponder number issued by Sunpass)
         * Required: false
         * Array: false
         */
        this.flSunpass = undefined;
        /**
         * Texas Department of Transportation TxTag ( this is the tolling device number issued by tolling authority TXTag)
         * Required: false
         * Array: false
         */
        this.txTag = undefined;
        /**
         * Harris County Toll Road Authority (this is the tolling device number issued by Harris Countin Toll Road Authority)
         * Required: false
         * Array: false
         */
        this.harrisCntyTollAuth = undefined;
        /**
         * PikePass (This is the tolling device number issued by 'Pikepass' toll system)
         * Required: false
         * Array: false
         */
        this.pikepassToll = undefined;
        /**
         * This is the 6 digit profit center number for a location in Oracle.  12/13/2019 increased length from 3 to 6, and removed SIC from name
         * Required: false
         * Array: false
         */
        this.profitCenterCd = undefined;
        /**
         * It allows us to identify a subset of particular vehicles that dont fit into any group we can do an inquiry on.  For example if LTL sends  a list of 150 various tractors they want to sell. A unique identifier is assiigned as the subset cd in this field (say 1234) in order to pull a list of those specific 100 tractors with any info.
         * Required: false
         * Array: false
         */
        this.subsetId = undefined;
        /**
         * Indicates if the equipment needs to be updated or created as new.
         * Required: false
         * Array: false
         */
        this.actionCd = undefined;
        /**
         * Stores CSV Upload Equipment Comment.
         * Required: false
         * Array: false
         */
        this.comment = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Stores exception details of any rows that failed to upload.  Added 02/04/2020
 */
class EquipmentCsvException {
    constructor() {
        /**
         * Unique ID assigned by system to a CSV File Upload when the upload occurs and the metadata row is created.
         * Required: false
         * Array: false
         */
        this.uploadId = undefined;
        /**
         * Identifies an equipment
         * Required: false
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         * Part of unique identifier.
         * Required: false
         * Array: false
         */
        this.exceptionSequenceNbr = undefined;
        /**
         * The HTTP error code for this fault. E.g. 404 for a Not Found condition.
         * Required: false
         * Array: false
         */
        this.faultCd = undefined;
        /**
         * Stores codes that identify particular errors.
         * Required: false
         * Array: false
         */
        this.errorCd = undefined;
        /**
         * Stores description error message provided by the ERROR_CD field.
         * Required: false
         * Array: false
         */
        this.message = undefined;
        /**
         * Stores exception message provided by JAVA.
         * Required: false
         * Array: false
         */
        this.trace = undefined;
        /**
         * Stores a list of extra error information provided by business logic.
         * Required: false
         * Array: false
         */
        this.moreInformation = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class EquipmentCsvExceptionHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.uploadId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exceptionSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.faultCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.errorCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.message = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trace = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.moreInformation = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
    }
}
/**
 *
 */
class EquipmentCsvHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.uploadId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.chargeSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.installSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.licenseCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.licenseStateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.licensePlateNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.plateTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.expireDateYyyymm = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.fleetCodeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.grossWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.taxWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentYrYyyy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentMake = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.vinNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.fuelTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.axlesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bodyCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.emptyWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.vehicleCostAmount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.factoryCostAmount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.purchaseDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sellDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.priorEquipmentNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.titleStateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.titleNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentReceiveYrYyyy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.retireYrYyyy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ownedLeaseCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.apportionedCode = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lessorInformation = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.newyorkTaxSeries = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentModelNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.retiredUnit = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replacedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.corpCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.domicileStateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.titleOutInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hvutTaxAmount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hvutTaxDateYyyymm = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.salesTaxStateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.salesTaxDateYyyymm = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.salesTaxComment = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.salesTaxAmount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.prevPlateNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.weightGroupCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ezPass = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nyHwyUseTaxNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.usTransponderNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.vehicleRefCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.weightStnPrepass = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.weightStnNorpass = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.flSunpass = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.txTag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.harrisCntyTollAuth = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pikepassToll = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.profitCenterCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.subsetId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.actionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.comment = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
    }
}
/**
 *
 */
class EquipmentHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.chargeSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.installSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.licenseCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.licenseStateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.licensePlateNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.plateTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.expireDateYyyymm = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.fleetCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.grossWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.taxWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentYrYyyy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentMake = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.vinNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.fuelTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.axlesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bodyCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.emptyWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.vehicleCostAmount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.factoryCostAmount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.purchaseDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sellDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.priorEquipmentNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.titleStateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.titleNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentReceiveYrYyyy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.retireYrYyyy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ownedLeaseCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.apportionedCode = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lessorInformation = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.newyorkTaxSeries = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentModelNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.retiredUnit = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replacedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.corpCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.domicileStateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.titleOutInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hvutTaxAmount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hvutTaxDateYyyymm = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.salesTaxStateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.salesTaxDateYyyymm = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.salesTaxComment = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.salesTaxAmount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.prevPlateNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.weightGroupCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ezPass = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nyHwyUseTaxNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.usTransponderNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.vehicleRefCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.weightStnPrepass = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.weightStnNorpass = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.flSunpass = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.txTag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.harrisCntyTollAuth = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pikepassToll = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.profitCenterCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.subsetId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.inServiceDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.planPurchaseYearYyyy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduledRetireYearYyyy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.servicedBySicCd = undefined;
    }
}
/**
 * Record information about fleet code details
 */
class FleetCode {
    constructor() {
        /**
         * Unique Code for Fleet
         * Required: false
         * Array: false
         */
        this.fleetCd = undefined;
        /**
         * Fleet code description
         * Required: false
         * Array: false
         */
        this.descr = undefined;
        /**
         * State where fleet mainly operates
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         * A (Annual)
         * I (Interstate)
         * P (Permanent)
         * M (Multiyear).
         * Required: false
         * Array: false
         */
        this.plateTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * business unite.g. CCX legacy Con-way
         * Required: false
         * Array: false
         */
        this.parentCorpCd = undefined;
    }
}
/**
 *
 */
class FleetCodeHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.fleetCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.descr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.corpCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.plateTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
    }
}
/**
 * Details about employees that process and approve claims.
 */
class InterfaceEmployee {
    constructor() {
        /**
         * Employee Id of the Claims employee.
         * Required: false
         * Array: false
         */
        this.employeeId = undefined;
        /**
         * First Name of the Claims employee.
         * Required: false
         * Array: false
         */
        this.firstName = undefined;
        /**
         * Middle Name of the Supervisor of the Claims employee.
         * Required: false
         * Array: false
         */
        this.middleName = undefined;
        /**
         * Last Name of the Supervisor of the Claims employee.
         * Required: false
         * Array: false
         */
        this.lastName = undefined;
        /**
         * Job Description of the Supervisor of the Claims employee.
         * Required: false
         * Array: false
         */
        this.jobDescription = undefined;
        /**
         * The domicile SIC location for the employee.
         * Required: false
         * Array: false
         */
        this.domicileSicCd = undefined;
        /**
         * The employee id of this employee's supervisor.
         * Required: false
         * Array: false
         */
        this.supervisorEmployeeId = undefined;
        /**
         * Desc: The business phone number of the employee.  It is sourced from HRS_EMPL_PHONE_VW in DB2 for typ_cd = 'B'.  01/10/2019 Added by Cathy
         * Required: false
         * Array: false
         */
        this.businessPhoneTxt = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of InterfaceEmployee. Contains the details at the time on an event. It also provides the previous values if an update occured
 * Details about employees that process and approve claims.
 */
class InterfaceEmployeeSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         * Employee Id of the Claims employee.
         * Required: false
         * Array: false
         */
        this.employeeId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 * Record all information about License codes used for the equipment on the App.
 *
 */
class LicenseCode {
    constructor() {
        /**
         * License Code number. Unique code identifies a row.
         * Required: false
         * Array: false
         */
        this.licenseCd = undefined;
        /**
         * State for the License
         * Required: false
         * Array: false
         */
        this.domicileStateCd = undefined;
        /**
         * Fleet Code number
         * Required: false
         * Array: false
         */
        this.licFleetCodeId = undefined;
        /**
         * Type of Vehicule. values it store: 'P' for Power unit or 'T' for trailer)
         * Required: false
         * Array: false
         */
        this.vehicleTypeCd = undefined;
        /**
         * License Code description
         * Required: false
         * Array: false
         */
        this.descr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Corporation name. Example: RSI, MLG (Menlo legacy)
         * Required: false
         * Array: false
         */
        this.parentCorpCd = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.licenseCodeRequirement = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.licenseCodeState = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.licenseCodeStateOperations = undefined;
    }
}
/**
 *
 */
class LicenseCodeHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.licenseCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.corpCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.domicileStateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.licFleetCodeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.vehicleTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.descr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
    }
}
/**
 * Define relationship between License Code and Requirement Code.
 */
class LicenseCodeRequirement {
    constructor() {
        /**
         * Foreign key. Identifies the License code
         * Required: false
         * Array: false
         */
        this.licenseCd = undefined;
        /**
         * Foreign key. Identifies the Requirement code
         * Required: false
         * Array: false
         */
        this.requirementCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class LicenseCodeRequirementHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.licenseCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.requirementCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
    }
}
/**
 * Record Weight information by State
 */
class LicenseCodeState {
    constructor() {
        /**
         * Foreign key. Identifies the License code
         * Required: false
         * Array: false
         */
        this.licenseCd = undefined;
        /**
         * State code
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         * Weight limit of the state
         * Required: false
         * Array: false
         */
        this.weightLbs = undefined;
        /**
         * Weither weight limit exists Y-yes, N-No
         * Required: false
         * Array: false
         */
        this.weightExistsInd = undefined;
        /**
         * Condition to show or not on the registration. there is a check mark if weight over 80,000 will not show on registration
         * Required: false
         * Array: false
         */
        this.weightOverInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class LicenseCodeStateHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.licenseCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.weightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.weightExistsInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.weightOverInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
    }
}
/**
 * Table needed to store states that are allowed to operate with each License Code.  Added 01/23/2020
 */
class LicenseCodeStateOperations {
    constructor() {
        /**
         * License Code number. Unique code identifies a row.
         * Required: false
         * Array: false
         */
        this.licenseCd = undefined;
        /**
         * State code where the license may be operated.
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class LicenseCodeStateOperationsHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.licenseCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
    }
}
/**
 * Special history table that only contains plate information.  Added 09/27/2023 LISG-55063
 */
class PlateHist {
    constructor() {
        /**
         * Identifies an equipment
         * Required: false
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         * Part of PK
         * Required: false
         * Array: false
         */
        this.histCreatedDateTime = undefined;
        /**
         * License plate number
         * Required: false
         * Array: false
         */
        this.licensePlateNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histActionCd = undefined;
        /**
         * EZ Pass (toll collection system) assigned to the equipment. Added 10/31/2023  LISG-55322
         * Required: false
         * Array: false
         */
        this.ezPass = undefined;
        /**
         * PrePass (Is weigh station bypass and tolling device) assigned to the equipment.  Added 10/31/2023  LISG-55322
         * Required: false
         * Array: false
         */
        this.weightStationPrepass = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Record License Requirement information.
 */
class Requirement {
    constructor() {
        /**
         * Requirement Code. Uniquely identies a requirement
         * Required: false
         * Array: false
         */
        this.requirementCd = undefined;
        /**
         * Name of Requirement
         * Required: false
         * Array: false
         */
        this.requirementName = undefined;
        /**
         * State Code
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         * Number of Account
         * Required: false
         * Array: false
         */
        this.accountInfo = undefined;
        /**
         * Requirement Expiradtion date
         * Required: false
         * Array: false
         */
        this.expireDtYyyymm = undefined;
        /**
         * Requirement Enforcement date
         * Required: false
         * Array: false
         */
        this.enforcementDtYyyymm = undefined;
        /**
         * Requirement Renewal month
         * Required: false
         * Array: false
         */
        this.renewalMonth = undefined;
        /**
         * For who this requirement is applicable
         * Required: false
         * Array: false
         */
        this.applicableTo = undefined;
        /**
         * Credential Issued
         * Required: false
         * Array: false
         */
        this.credentialIssuedInfo = undefined;
        /**
         * Agency name
         * Required: false
         * Array: false
         */
        this.agencyName = undefined;
        /**
         * Agency Address
         * Required: false
         * Array: false
         */
        this.agencyAddress = undefined;
        /**
         * Agency Address 2
         * Required: false
         * Array: false
         */
        this.agencyAddress2 = undefined;
        /**
         * Agency City
         * Required: false
         * Array: false
         */
        this.agencyCity = undefined;
        /**
         * Agency State
         * Required: false
         * Array: false
         */
        this.agencyState = undefined;
        /**
         * Agency Zip Code
         * Required: false
         * Array: false
         */
        this.agencyZip = undefined;
        /**
         * Agency Contact Name
         * Required: false
         * Array: false
         */
        this.agencyContactName = undefined;
        /**
         * Agency Contact Phone
         * Required: false
         * Array: false
         */
        this.agencyContactPhone = undefined;
        /**
         * Agency Contact Name 2
         * Required: false
         * Array: false
         */
        this.agencyContactName2 = undefined;
        /**
         * Agency Contact Phone 2
         * Required: false
         * Array: false
         */
        this.agencyContactPhone2 = undefined;
        /**
         * Fee For Permit (Cost of the permit.  For example permit might be $10 per vehicle)
         * Required: false
         * Array: false
         */
        this.feeForPermit = undefined;
        /**
         * Temp Qualification (Tells us if state offers a 'temp permit' maybe good for one trip rather than full year)
         * Required: false
         * Array: false
         */
        this.tempQualification = undefined;
        /**
         * email address
         * Required: false
         * Array: false
         */
        this.emailAddress = undefined;
        /**
         * web address
         * Required: false
         * Array: false
         */
        this.webAddress = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.requirementComment = undefined;
    }
}
/**
 * Store comments regarding Requirement  and also further replies to the comments, thus building chain of comments
 */
class RequirementComment {
    constructor() {
        /**
         * Identifies the requirement
         * Required: false
         * Array: false
         */
        this.requirementCd = undefined;
        /**
         * For a given requirement this uniquely identifies a comment
         * Required: false
         * Array: false
         */
        this.seqNbr = undefined;
        /**
         * Comment Text
         * Required: false
         * Array: false
         */
        this.comment = undefined;
        /**
         * This allows us to link thei comment to the previous comment of this conversation/thread for a given requirement.
         * 	Thus multiple threads /conversations of comments can be stored.
         * This identifies the comment for which this comment is the reply.
         * Note, the previous comment is assumed to be for the same requirement as this comment.
         * Required: false
         * Array: false
         */
        this.prevRequireCommentSeqNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Employee details for the create by employee id.
         * Required: false
         * Array: false
         */
        this.createByEmployeeDtl = undefined;
    }
}
/**
 *
 */
class RequirementCommentHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.requirementCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.seqNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.comment = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.prevRequireCommentSeqNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
    }
}
/**
 *
 */
class RequirementHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.requirementCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.requirementName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.accountInfo = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.expireDtYyyymm = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.enforcementDtYyyymm = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.renewalMonth = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.applicableTo = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.credentialIssuedInfo = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.agencyName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.agencyAddress = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.agencyAddress2 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.agencyCity = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.agencyState = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.agencyZip = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.agencyContactName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.agencyContactPhone = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.agencyContactName2 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.agencyContactPhone2 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.feeForPermit = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tempQualification = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.emailAddress = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.webAddress = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
    }
}
/**
 * Record that license permit is ending and allow app to send the alerts based on the reminders set in this table
 */
class RequirementProject {
    constructor() {
        /**
         * Uniquely identifies a requirement project
         * Required: false
         * Array: false
         */
        this.requirementProjectId = undefined;
        /**
         * Expire date: Expire date on current permit.  For example if current permit expires on Dec 31 each year. 12/31 will show as expire date
         * Required: false
         * Array: false
         */
        this.expireDtYyyymm = undefined;
        /**
         * First Reminder date email alert
         * Required: false
         * Array: false
         */
        this.firstReminderDtYyyymm = undefined;
        /**
         * Second Reminder date email alert
         * Required: false
         * Array: false
         */
        this.secondReminderDtYyyymm = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * The Project will be assigned to the user responsible for the project renewal cycles Added on 08/28/23 LISG-54854
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * This column will store the username of the users to whom this project is assigned.Added on 08/30/23 LISG-54904
         * Required: false
         * Array: false
         */
        this.userName = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Foreign key. Identifies requirement
         * Required: false
         * Array: false
         */
        this.parentRequirementCd = undefined;
        /**
         * Foreign key to LIC_CORP
         * Required: false
         * Array: false
         */
        this.parentCorpCd = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.corpCodeRequirementProject = undefined;
    }
}
/**
 *
 */
class RequirementProjectHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.requirementProjectId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.requirementCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.corpCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.expireDtYyyymm = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.firstReminderDtYyyymm = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.secondReminderDtYyyymm = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
    }
}
/**
 * record values of weight allowed for each weight group for all the states. US is currently divided in to 12 weight groups and it can vary in future.
 */
class StateWeightGroup {
    constructor() {
        /**
         * State code
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         * There are typically up to 12 weight groups for a state. Some states have fewer groups. E.g Arizona has two groups 8 and 12
         * Required: false
         * Array: false
         */
        this.weightGroupCd = undefined;
        /**
         * The weight allowed in that state for the weight group
         * Required: false
         * Array: false
         */
        this.weightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Stores control information for CSV File uploads for mass Licensing Equipment updates.  Added 02/03/2020
 *
 */
class UploadControlCsv {
    constructor() {
        /**
         * Unique ID assigned by system to a CSV File Upload when the upload occurs and the metadata row is created.
         * Required: false
         * Array: false
         */
        this.uploadId = undefined;
        /**
         * The status of the CSV upload processing.
         *
         * Valid values:
         * SUBMITTED
         * PROCESSING
         * COMPLETED
         * FAILED
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         * Size of CSV file in KB.  Added 07/19/2023  LISG-54591
         * Required: false
         * Array: false
         */
        this.csvSize = undefined;
        /**
         * Number of processed rows.   Added 07/19/2023  LISG-54591
         * Required: false
         * Array: false
         */
        this.processedRowCount = undefined;
        /**
         * Ensemble ID related to this upload.  Added 07/19/2023  LISG-54591
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
        /**
         * The name of the CSV file that was uploaded, if applicable.
         * Required: false
         * Array: false
         */
        this.csvFileName = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class UploadControlCsvHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.uploadId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.csvFileName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.uploadValidationProgressCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
    }
}
/**
 * Stores the errors encountered while uploading csv file.  Added 07/20/2023 LISG-54591
 */
class UploadCsvRecError {
    constructor() {
        /**
         * Unique ID assigned by system to a CSV File Upload when the upload occurs and the metadata row is created.
         * Required: false
         * Array: false
         */
        this.uploadId = undefined;
        /**
         * Part of PK.  This corresponds to the row number in the CSV file that is in error.
         * Required: false
         * Array: false
         */
        this.rowSequenceNbr = undefined;
        /**
         * Identifier of a piece of equipment - prefix + suffix
         * Required: false
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         * Message related to an errorthat occurred during upserting the equipment record.
         * Required: false
         * Array: false
         */
        this.errorMessage = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class EventWait {
    constructor() {
        /**
         * Unique identifier of records in this table. Values populated from sequence
         * Required: false
         * Array: false
         */
        this.waitInstanceId = undefined;
        /**
         * Id of the ensemble that is being put on lock wait
         * Required: false
         * Array: false
         */
        this.ensembleInstanceId = undefined;
        /**
         * Name of the queue that was the source of this ensemble message
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
        /**
         * The timestamp of when this ensemble was first put on lock wait
         * Required: false
         * Array: false
         */
        this.waitStartTmst = undefined;
        /**
         * The number of times this ensemble was put on lock wait
         * Required: false
         * Array: false
         */
        this.waitCount = undefined;
        /**
         * The duration of wait in milliseconds
         * Required: false
         * Array: false
         */
        this.waitTime = undefined;
        /**
         * The maximum wait time in milliseconds before the lock will be overridden
         * Required: false
         * Array: false
         */
        this.maxWaitTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class RetriggerEvent {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventRetriggerId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.regionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceTableName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.transactionId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Details of a symphony message
 */
class SymphonyMessage {
    constructor() {
        /**
         * ID of the message
         * Required: false
         * Array: false
         */
        this.messageId = undefined;
        /**
         * Timestamp of when the message was enqueued
         * Required: false
         * Array: false
         */
        this.enqueueTmst = undefined;
        /**
         * Number of time the message was retried
         * Required: false
         * Array: false
         */
        this.retryCount = undefined;
        /**
         * Message payload
         * Required: false
         * Array: false
         */
        this.payload = undefined;
    }
}
/**
 * Details about a message queue. E.g. Oracle Advanced Queue
 */
class SymphonyQueue {
    constructor() {
        /**
         * Name of the queue
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Maximum number of retries
         * Required: false
         * Array: false
         */
        this.maxRetries = undefined;
        /**
         * Delay for retries
         * Required: false
         * Array: false
         */
        this.retryDelay = undefined;
        /**
         * Indicates if enqueue is enabled
         * Required: false
         * Array: false
         */
        this.enqueueEnabled = undefined;
        /**
         * Indicates if dequeue is enabled
         * Required: false
         * Array: false
         */
        this.dequeueEnabled = undefined;
        /**
         * The retention time
         * Required: false
         * Array: false
         */
        this.retention = undefined;
    }
}
/**
 * Captures transaction events. API writes one record per transaction.  Added to EVT seed model  06/26/2020
 */
class TransactionEvent {
    constructor() {
        /**
         * PK. Unique ID of the transaction. For Oracle, use the transaction id available in the context. Populated by trigger.
         * Required: false
         * Array: false
         */
        this.transactionId = undefined;
        /**
         * Identifies the type of transaction. Could be method name
         * Required: false
         * Array: false
         */
        this.transactionType = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * Number of seconds to hold event before publishing.  Added 09/21/2020  LISG-7498
         * Required: false
         * Array: false
         */
        this.eventReleaseDelaySeconds = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.transactionDetails = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.transactionBusinessEvents = undefined;
    }
}
/**
 *
 */
class TransactionEventBusiness {
    constructor() {
        /**
         * PK. Unique identifier for the record.  This will be populate by a trigger before insert to get the ID from EVT_TRANSACTION_BUS_SEQ
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * The transaction id from the session.  This will be populate by a trigger before insert.
         * Required: false
         * Array: false
         */
        this.transactionId = undefined;
        /**
         * Short description of the business transaction type.
         * Required: false
         * Array: false
         */
        this.transactionType = undefined;
        /**
         * Code identifying the type of business event.
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         * Code identifying the type of business event.
         * Required: false
         * Array: false
         */
        this.subTypeCd = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions. In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Stores the snapshot of all the tables that were updated as part of the transaction. The snapshot is a json representation of all columns that were changed. The snapshot will be created by insert/update/delete triggers on the source table.   Added to EVT seed model  06/26/2020
 */
class TransactionEventDetail {
    constructor() {
        /**
         * PK. Unique id for the snapshot record. Generated by Sequence.
         * Required: false
         * Array: false
         */
        this.detailId = undefined;
        /**
         * Unique ID of the transaction. For Oracle, use the transaction id available in the context. Populated by trigger.
         * Required: false
         * Array: false
         */
        this.transactionId = undefined;
        /**
         * Name of the table for which the snapshot is stored
         * Required: false
         * Array: false
         */
        this.tableName = undefined;
        /**
         * Json string containing the before and after values for all the columns that were changed by the transaction.
         * Required: false
         * Array: false
         */
        this.snapshot = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Identifies the type of transaction. Could be method name
         * Required: false
         * Array: false
         */
        this.transactionType = undefined;
    }
}
class BulkUpsertEquipmentRqst {
    constructor() {
        /**
         * The list of Equipment to be created or updated.
         * Required: true
         * Array: true
         */
        this.equipment = undefined;
        /**
         * The unique identifier of the upload for the list of equipment.
         * Required: false
         * Array: false
         */
        this.uploadId = undefined;
    }
}
class BulkUpsertEquipmentResp {
    constructor() {
        /**
         * The list of Equipment that were upserted successfully.
         * Required: false
         * Array: true
         */
        this.equipment = undefined;
        /**
         * The list of errors that are related to equipment that were not upserted successfully.
         * Required: false
         * Array: true
         */
        this.equipmentValidationErrors = undefined;
    }
}
/**
 *
 */
class CompareLicenseCodesRqst {
    constructor() {
        /**
         * The unique identifier of the old License Code.
         * Required: true
         * Array: false
         */
        this.oldLicenseCd = undefined;
        /**
         * The unique identifier of the new License Code.
         * Required: true
         * Array: false
         */
        this.newLicenseCd = undefined;
    }
}
/**
 *
 */
class CompareLicenseCodesResp {
    constructor() {
        /**
         * The old License Code's description.
         * Required: false
         * Array: false
         */
        this.oldLicenseCodeDescr = undefined;
        /**
         * The new License Code's description.
         * Required: false
         * Array: false
         */
        this.newLicenseCodeDescr = undefined;
        /**
         * The list of requirements that are linked to the old License Code.
         * Required: false
         * Array: true
         */
        this.oldLicenseCodeRequirements = undefined;
        /**
         * The list of requirements that are linked to the new License Code.
         * Required: false
         * Array: true
         */
        this.newLicenseCodeRequirements = undefined;
        /**
         * The list of requirements that are present in the new License Code, but not the old one.
         * Required: false
         * Array: true
         */
        this.comparedRequirements = undefined;
        /**
         * The list of states and their weight comparison changes for the old and new License Code with respect to its weight group requirement.
         * Required: false
         * Array: true
         */
        this.stateWeightGroupComparisons = undefined;
    }
}
/**
 *
 */
class CompareStateWeightGroupsRqst {
    constructor() {
        /**
         * The code associated with the old weight group.
         * Required: true
         * Array: false
         */
        this.oldWeightGroupdCd = undefined;
        /**
         * The code associated with the new weight group.
         * Required: true
         * Array: false
         */
        this.newWeightGroupCd = undefined;
    }
}
/**
 *
 */
class CompareStateWeightGroupsResp {
    constructor() {
        /**
         * The list of the state weight comparison results for the old and new weight group.
         * Required: false
         * Array: true
         */
        this.stateWeightGroupComparisons = undefined;
    }
}
/**
 *
 */
class CreateCorpRqst {
    constructor() {
        /**
         * The Corp that will be created.
         * Required: true
         * Array: false
         */
        this.corp = undefined;
    }
}
/**
 *
 */
class CreateCorpResp {
    constructor() {
        /**
         * The Corp that was created.
         * Required: false
         * Array: false
         */
        this.corp = undefined;
    }
}
/**
 *
 */
class CreateCorpCodeRequirementProjectRqst {
    constructor() {
        /**
         * The Corp Code Requirement Project that will be created.
         * Required: true
         * Array: false
         */
        this.corpCodeRequirementProject = undefined;
        /**
         * The unique identifier of a Requirement Project.
         * Required: false
         * Array: false
         */
        this.requirementProjectId = undefined;
    }
}
/**
 *
 */
class CreateCorpCodeRequirementProjectResp {
    constructor() {
        /**
         * The Corp Code Requirement Project that was created.
         * Required: false
         * Array: false
         */
        this.corpCodeRequirementProject = undefined;
    }
}
/**
 * Create Equipment record based on input object
 */
class CreateEquipmentRqst {
    constructor() {
        /**
         * The code that represents the fleet to which the equipment is associated.
         * Required: true
         * Array: false
         */
        this.equipment = undefined;
        /**
         * The code that represents the fleet to which the equipment is associated.
         * Required: false
         * Array: false
         */
        this.fleetCd = undefined;
        /**
         * The license code search criteria.
         * Required: false
         * Array: false
         */
        this.licenseCd = undefined;
        /**
         * The content used to search in the comments.
         * Required: false
         * Array: false
         */
        this.comment = undefined;
    }
}
/**
 * Response to the request to Create Equipment record based on input object
 */
class CreateEquipmentResp {
    constructor() {
        /**
         * Equipment object that was created
         * Required: false
         * Array: false
         */
        this.equipment = undefined;
        /**
         * This is the comment that was added upon initial creation.
         * Required: false
         * Array: false
         */
        this.comment = undefined;
    }
}
class CreateEquipmentCommentsRqst {
    constructor() {
        /**
         * The list of EquipmentComment records which will be created.
         * Required: true
         * Array: true
         */
        this.equipmentComments = undefined;
        /**
         * The standard audit trail information.
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
class CreateEquipmentCommentsResp {
    constructor() {
        /**
         * The list of EquipmentComment records that were created.
         * Required: false
         * Array: true
         */
        this.equipmentComments = undefined;
    }
}
/**
 *
 */
class CreateFleetCdRqst {
    constructor() {
        /**
         * The Fleet Code that will be created.
         * Required: true
         * Array: false
         */
        this.fleetCode = undefined;
    }
}
/**
 *
 */
class CreateFleetCdResp {
    constructor() {
        /**
         * The Fleet Code that was created.
         * Required: false
         * Array: false
         */
        this.fleetCode = undefined;
    }
}
/**
 *
 */
class CreateLicenseCdRqst {
    constructor() {
        /**
         * The License Code that will be created.
         * Required: true
         * Array: false
         */
        this.licenseCode = undefined;
        /**
         * The original licenseCd from which the new license code request is cloned.
         * Required: false
         * Array: false
         */
        this.clonedLicenseCd = undefined;
    }
}
/**
 *
 */
class CreateLicenseCdResp {
    constructor() {
        /**
         * The License Code that was created.
         * Required: false
         * Array: false
         */
        this.licenseCode = undefined;
    }
}
/**
 *
 */
class CreateRequirementRqst {
    constructor() {
        /**
         * The Requirement that will be created.
         * Required: true
         * Array: false
         */
        this.requirement = undefined;
    }
}
/**
 *
 */
class CreateRequirementResp {
    constructor() {
        /**
         * The Requirement that was created.
         * Required: false
         * Array: false
         */
        this.requirement = undefined;
    }
}
/**
 *
 */
class CreateRequirementCommentRqst {
    constructor() {
        /**
         * The Requirement Comment that will be created.
         * Required: true
         * Array: false
         */
        this.requirementComment = undefined;
    }
}
/**
 *
 */
class CreateRequirementCommentResp {
    constructor() {
        /**
         * The Requirement Comment that was created.
         * Required: false
         * Array: false
         */
        this.requirementComment = undefined;
    }
}
/**
 *
 */
class CreateRequirementProjectRqst {
    constructor() {
        /**
         * The Requirement Project that will be created.
         * Required: false
         * Array: false
         */
        this.requirementProject = undefined;
    }
}
/**
 *
 */
class CreateRequirementProjectResp {
    constructor() {
        /**
         * The Requirement Project that was created.
         * Required: false
         * Array: false
         */
        this.requirementProject = undefined;
    }
}
/**
 *
 */
class CreateUploadControlCsvRqst {
    constructor() {
        /**
         * The UploadControlCsv to be created.
         * Required: true
         * Array: false
         */
        this.uploadControlCsv = undefined;
    }
}
/**
 *
 */
class CreateUploadControlCsvResp {
    constructor() {
        /**
         * The UploadControlCsv that was created.
         * Required: false
         * Array: false
         */
        this.uploadControlCsv = undefined;
    }
}
/**
 *
 */
class CreateUploadCsvRecErrorsRqst {
    constructor() {
        /**
         * The list of UploadCsvRecErrors to be created.
         * Required: true
         * Array: true
         */
        this.uploadCsvRecErrors = undefined;
    }
}
/**
 *
 */
class CreateUploadCsvRecErrorsResp {
    constructor() {
        /**
         * The list of UploadCsvRecErrors that were created.
         * Required: false
         * Array: true
         */
        this.uploadCsvRecErrors = undefined;
    }
}
/**
 * Path parameter used to delete an Equipment record.
 */
class DeleteEquipmentPath {
    constructor() {
        /**
         * The unique identifier for the Equipment record.
         * Required: false
         * Array: false
         */
        this.equipmentNbr = undefined;
    }
}
/**
 * Path parameters used to identify the EquipmentComment record that will be deleted.
 */
class DeleteEquipmentCommentPath {
    constructor() {
        /**
         * The unique identifier for the Equipment record.  It is part of the composite primary key for EquipmentComment.
         * Required: true
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         * The sequence number associated with the EquipmentComment.  It is part of the composite primary key for EquipmentComment.
         * Required: true
         * Array: false
         */
        this.sequenceNbr = undefined;
    }
}
/**
 *
 */
class DeleteFleetCdPath {
    constructor() {
        /**
         * The unique identifier for the Fleet Code.
         * Required: true
         * Array: false
         */
        this.fleetCd = undefined;
    }
}
/**
 *
 */
class DeleteLicenseCdPath {
    constructor() {
        /**
         * The unique identifier for the License Code.
         * Required: true
         * Array: false
         */
        this.licenseCd = undefined;
    }
}
/**
 *
 */
class DeleteRequirementPath {
    constructor() {
        /**
         * The unique identifier for a Requirement.
         * Required: true
         * Array: false
         */
        this.requirementCd = undefined;
    }
}
/**
 *
 */
class DeleteRequirementCommentPath {
    constructor() {
        /**
         * The unique identifier for a Requirement.
         * Required: true
         * Array: false
         */
        this.requirementCd = undefined;
        /**
         * The unique identifier for a comment for a given requirement.
         * Required: true
         * Array: false
         */
        this.seqNbr = undefined;
    }
}
/**
 *
 */
class DeleteRequirementProjectPath {
    constructor() {
        /**
         * The unique identifier for a Requirement Project.
         * Required: true
         * Array: false
         */
        this.requirementProjectId = undefined;
    }
}
/**
 * Path parameter used to request the domicile state and weight groups.
 */
class GetDomicileStateAndWeightGroupsPath {
    constructor() {
        /**
         * The license code that is associated with a domicile state and weight groups.
         * Required: true
         * Array: false
         */
        this.licenseCd = undefined;
    }
}
/**
 * Response the request of weight group and domicile state from license code.
 */
class GetDomicileStateAndWeightGroupsResp {
    constructor() {
        /**
         * The domicile's state code.
         * Required: false
         * Array: false
         */
        this.domicileStateCd = undefined;
        /**
         * The weight groups associated with the licenseCd provided.
         * Required: true
         * Array: true
         */
        this.weightGroupCds = undefined;
    }
}
/**
 * Path parameter used to retrieve an Equipment record.
 */
class GetEquipmentPath {
    constructor() {
        /**
         * The unique identifier for the Equipment.
         * Required: true
         * Array: false
         */
        this.equipmentNbr = undefined;
    }
}
/**
 * Response to the request to retrieve equipment based on equipmentNbr provided.
 */
class GetEquipmentResp {
    constructor() {
        /**
         * The equipment record associated with the equipmentNbr provided.
         * Required: false
         * Array: false
         */
        this.equipment = undefined;
    }
}
/**
 * Path parameters used to retrieve an EquipmentComment record.
 */
class GetEquipmentCommentPath {
    constructor() {
        /**
         * The unique identifier for the Equipment record.  It is part of the composite primary key for EquipmentComment.
         * Required: true
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         * The sequence number associated with the EquipmentComment.  It is part of the composite primary key for EquipmentComment.
         * Required: true
         * Array: false
         */
        this.sequenceNbr = undefined;
    }
}
/**
 * Response to the request to retrieve an EquipmentComment based on the equipmentNbr and sequenceNbr provided.
 */
class GetEquipmentCommentResp {
    constructor() {
        /**
         * The EquipmentComment record associated with the equipmentNbr and sequenceNbr provided.
         * Required: false
         * Array: false
         */
        this.equipmentComment = undefined;
    }
}
/**
 *
 */
class GetCorpPath {
    constructor() {
        /**
         * The code associated with a corporation which will be validated and have its corresponding Corp returned.
         * Required: true
         * Array: false
         */
        this.corpCd = undefined;
    }
}
/**
 *
 */
class GetCorpResp {
    constructor() {
        /**
         * The Corp associated with the corpCd.
         * Required: false
         * Array: false
         */
        this.corp = undefined;
        /**
         * Returns true if the corpCd provided is valid.
         * Required: false
         * Array: false
         */
        this.isValid = undefined;
    }
}
/**
 *
 */
class GetCorpCodeRequirementProjectPath {
    constructor() {
        /**
         * The unique identifier for a Completion Data. It will be validated and have its corresponding Completion Data returned.
         * Required: true
         * Array: false
         */
        this.corpCodeRequirementProjectId = undefined;
    }
}
/**
 *
 */
class GetCorpCodeRequirementProjectResp {
    constructor() {
        /**
         * The Corp Code Requirement Project that is associated with the corpCodeRequirementProjectId provided.
         * Required: false
         * Array: false
         */
        this.corpCodeRequirementProject = undefined;
    }
}
/**
 *
 */
class GetFleetCdPath {
    constructor() {
        /**
         * The unique identifier for a Fleet Code.
         * Required: true
         * Array: false
         */
        this.fleetCd = undefined;
    }
}
/**
 *
 */
class GetFleetCdResp {
    constructor() {
        /**
         * The Fleet Code associated with the fleetCd.
         * Required: false
         * Array: false
         */
        this.fleetCode = undefined;
    }
}
/**
 *
 */
class GetLicenseCdPath {
    constructor() {
        /**
         * The unique identifier for a LicenseCode.  It will be validated and have its corresponding LicenseCode returned.
         * Required: true
         * Array: false
         */
        this.licenseCd = undefined;
    }
}
/**
 *
 */
class GetLicenseCdResp {
    constructor() {
        /**
         * The LicenseCode associated with the licenseCd.
         * Required: false
         * Array: false
         */
        this.licenseCode = undefined;
        /**
         * Indicates whether the licenseCd provided is valid.
         * Required: false
         * Array: false
         */
        this.isValid = undefined;
    }
}
/**
 *
 */
class GetRequirementPath {
    constructor() {
        /**
         * The unique identifier for a Requirement.
         * Required: true
         * Array: false
         */
        this.requirementCd = undefined;
    }
}
/**
 *
 */
class GetRequirementResp {
    constructor() {
        /**
         * The Requirement associated with the requirementCd provided.
         * Required: false
         * Array: false
         */
        this.requirement = undefined;
    }
}
/**
 *
 */
class GetRequirementProjectPath {
    constructor() {
        /**
         * The unique identifier for a Requirement Project.
         * Required: true
         * Array: false
         */
        this.requirementProjectId = undefined;
    }
}
/**
 *
 */
class GetRequirementProjectResp {
    constructor() {
        /**
         * The Requirement Project associated with the requirementProjectId provided.
         * Required: false
         * Array: false
         */
        this.requirementProject = undefined;
    }
}
/**
 *
 */
class ListCorpsResp {
    constructor() {
        /**
         * The list of Corps.
         * Required: false
         * Array: true
         */
        this.corps = undefined;
    }
}
class ListEquipmentRqst {
    constructor() {
        /**
         * The unique identifiers for equipment.
         * Required: false
         * Array: true
         */
        this.equipmentNbrs = undefined;
        /**
         * The list of values that represent standard industrial classification codes assigned to equipment.
         * Required: false
         * Array: true
         */
        this.chargeSicCds = undefined;
        /**
         * The list of service center identification codes that represent the locations where the equipment is operated.
         * Required: false
         * Array: true
         */
        this.installSicCds = undefined;
        /**
         * The list of unique identifiers for equipment license codes.
         * Required: false
         * Array: true
         */
        this.licenseCds = undefined;
        /**
         * The list of license states codes for equipment.
         * Required: false
         * Array: true
         */
        this.licenseStateCds = undefined;
        /**
         * The list of license plate numbers for equipment.
         * Required: false
         * Array: true
         */
        this.licensePlateNbrs = undefined;
        /**
         * The list of codes associated with plate types for equipment.
         * Required: false
         * Array: true
         */
        this.plateTypeCds = undefined;
        /**
         * The equipment expiration dates, which are represented as a year and a month.
         * Required: false
         * Array: true
         */
        this.expireDateYyyymm = undefined;
        /**
         * The list of codes that are associated with the equipment fleets.
         * Required: false
         * Array: true
         */
        this.fleetCds = undefined;
        /**
         * The list of complete gross weights for the equipment.
         * Required: false
         * Array: true
         */
        this.grossWeightLbs = undefined;
        /**
         * The list of tax weights for the equipment.
         * Required: false
         * Array: true
         */
        this.taxWeightLbs = undefined;
        /**
         * The list of manufactured years for the equipment.
         * Required: false
         * Array: true
         */
        this.equipmentYrYyyy = undefined;
        /**
         * The list of manufacturer makes for the equipment.
         * Required: false
         * Array: true
         */
        this.equipmentMakes = undefined;
        /**
         * The list of manufacturer serial numbers for the equipment.
         * Required: false
         * Array: true
         */
        this.vinNbrs = undefined;
        /**
         * The list of codes associated with the fuel types for the equipment.
         * Required: false
         * Array: true
         */
        this.fuelTypeCds = undefined;
        /**
         * The list of axle counts for the equipment.
         * Required: false
         * Array: true
         */
        this.axlesCounts = undefined;
        /**
         * The list of codes associated with the equipment bodies.
         * Required: false
         * Array: true
         */
        this.bodyCds = undefined;
        /**
         * The list of codes associated with the types of equipment.
         * Required: false
         * Array: true
         */
        this.equipmentTypeCds = undefined;
        /**
         * The list of equipment weights without charge weights.
         * Required: false
         * Array: true
         */
        this.emptyWeightLbs = undefined;
        /**
         * The list of cost amounts for the equipment.
         * Required: false
         * Array: true
         */
        this.vehicleCostAmounts = undefined;
        /**
         * The list of factory cost amounts for the equipment.
         * Required: false
         * Array: true
         */
        this.factoryCostAmounts = undefined;
        /**
         * The purchase date for equipment.
         * Required: false
         * Array: false
         */
        this.purchaseDate = undefined;
        /**
         * The date when the equipment was sold by XPO.
         * Required: false
         * Array: false
         */
        this.sellDate = undefined;
        /**
         * The list of prior equipment numbers for when equipment changes its purpose.
         * Required: false
         * Array: true
         */
        this.priorEquipmentNbrs = undefined;
        /**
         * The list of state codes that issued the equipment titles.
         * Required: false
         * Array: true
         */
        this.titleStateCds = undefined;
        /**
         * The list of title numbers for the equipment.
         * Required: false
         * Array: true
         */
        this.titleNbrs = undefined;
        /**
         * The list of years that equipment was received.
         * Required: false
         * Array: true
         */
        this.equipmentReceiveYrYyyy = undefined;
        /**
         * The list of years that equipment was retired.
         * Required: false
         * Array: true
         */
        this.retireYrYyyy = undefined;
        /**
         * The list of codes which determine whether the equipment is owned or leased.
         * Required: false
         * Array: true
         */
        this.ownedLeaseCds = undefined;
        /**
         * The list of apportioned codes for the equipment. The codes are formatted as three characters.  The first two characters represent the state the fleet is registered out of and the last digit is the year of the fleet registration.
         * Required: false
         * Array: true
         */
        this.apportionedCodes = undefined;
        /**
         * The list of lessors for the equipment.
         * Required: false
         * Array: true
         */
        this.lessorsInformation = undefined;
        /**
         * The list of New York tax related information for the equipment.
         * Required: false
         * Array: true
         */
        this.newyorkTaxSeries = undefined;
        /**
         * The list of model numbers for the equipment.
         * Required: false
         * Array: true
         */
        this.equipmentModelNbrs = undefined;
        /**
         * The list of equipment units which have been retired.
         * Required: false
         * Array: true
         */
        this.retiredUnits = undefined;
        /**
         * The list of equipment that replaced the previous equipment.
         * Required: false
         * Array: true
         */
        this.replacedBy = undefined;
        /**
         * The list of codes associated with the corporations for the equipment.  E.g. XPOMT, XPOSC, CNW (legacy Con-way).
         * Required: false
         * Array: true
         */
        this.corpCds = undefined;
        /**
         * The list of domicile state codes for the equipment.
         * Required: false
         * Array: true
         */
        this.domicileStateCds = undefined;
        /**
         * The indicator that represents the sent out status (to service center/DMV for updates/changes) of an equipment title.
         * Required: false
         * Array: false
         */
        this.titleOutInd = undefined;
        /**
         * The list of heavy vehicle use tax amounts for the equipment.
         * Required: false
         * Array: true
         */
        this.hvutTaxAmounts = undefined;
        /**
         * The heavy vehicle use tax dates for the equipment.
         * Required: false
         * Array: true
         */
        this.hvutTaxDateYyyymm = undefined;
        /**
         * The list of codes associated with the states where the equipment sales tax was paid.
         * Required: false
         * Array: true
         */
        this.salesTaxStateCds = undefined;
        /**
         * The list of dates when the equipment sales tax was paid.
         * Required: false
         * Array: true
         */
        this.salesTaxDateYyyymm = undefined;
        /**
         * The sales tax comment for the equipment.
         * Required: false
         * Array: false
         */
        this.salesTaxComment = undefined;
        /**
         * The list of sales tax amounts for the equipment.
         * Required: false
         * Array: true
         */
        this.salesTaxAmounts = undefined;
        /**
         * The list of previous plate numbers for the equipment.
         * Required: false
         * Array: true
         */
        this.prevPlateNbrs = undefined;
        /**
         * The list of weight group codes for the equipment. Each state allows certain weight groups, which range from W1 to W12.
         * Required: false
         * Array: true
         */
        this.weightGroupCds = undefined;
        /**
         * The list of equipment tolls for E-ZPass.
         * Required: false
         * Array: true
         */
        this.ezPasses = undefined;
        /**
         * The list of New York highway use tax numbers for the equipment.
         * Required: false
         * Array: true
         */
        this.nyHwyUseTaxNbrs = undefined;
        /**
         * The list of US transponder numbers for the equipment.
         * Required: false
         * Array: true
         */
        this.usTransponderNbrs = undefined;
        /**
         * The list of codes associated with the vehicle references for the equipment.
         * Required: false
         * Array: true
         */
        this.vehicleRefCds = undefined;
        /**
         * The list of values that represent equipment by weight station bypasses and tolling devices.
         * Required: false
         * Array: true
         */
        this.weightStnPrepasses = undefined;
        /**
         * The list of values that represent weight station bypass systems.
         * Required: false
         * Array: true
         */
        this.weightStnNorpasses = undefined;
        /**
         * The list of values that represent equipment by tolling transponder numbers issued by Sunpass.
         * Required: false
         * Array: true
         */
        this.flSunpasses = undefined;
        /**
         * The list of values that represent equipment by tags of the Texas Department of Transportation.  These are the tolling device numbers that are issued by tolling authority TXTag.
         * Required: false
         * Array: true
         */
        this.txTags = undefined;
        /**
         * The list of values that represent equipment by the tolling device numbers issued by the Harris County Toll Road Authority.
         * Required: false
         * Array: true
         */
        this.harrisCntyTollAuths = undefined;
        /**
         * The list of values that represent equipment by the tolling device numbers issued by the Pikepass toll system.
         * Required: false
         * Array: true
         */
        this.pikepassTolls = undefined;
        /**
         * The identifiers for subsets for equipment.  It enables identification of a subset of particular vehicles that don't fit into any group for which an inquiry can be performed.  For example, if LTL sends a list of 150 various tractors they want to sell, a unique identifier (e.g. 1234) is assigned as the subset code in this field in order to pull a list of those specific 100 tractors with any information.
         * Required: false
         * Array: true
         */
        this.subsetIds = undefined;
        /**
         * The list of codes that represent equipment profit centers.  This is the six digit number for a location in Oracle.
         * Required: false
         * Array: true
         */
        this.profitCenterCds = undefined;
        /**
         * The date when the equipment was put into service by XPO.
         * Required: false
         * Array: false
         */
        this.inServiceDate = undefined;
        /**
         * The beginning of the purchase date range.
         * Required: false
         * Array: false
         */
        this.purchaseDateFrom = undefined;
        /**
         * The ending of the purchase date range.
         * Required: false
         * Array: false
         */
        this.purchaseDateTo = undefined;
        /**
         * The beginning of the sell date range.
         * Required: false
         * Array: false
         */
        this.sellDateFrom = undefined;
        /**
         * The ending of the sell date range.
         * Required: false
         * Array: false
         */
        this.sellDateTo = undefined;
        /**
         * The beginning of the in service date range.
         * Required: false
         * Array: false
         */
        this.inServiceDateFrom = undefined;
        /**
         * The ending of the in service date range.
         * Required: false
         * Array: false
         */
        this.inServiceDateTo = undefined;
        /**
         * The content to search for in the comments.
         * Required: false
         * Array: false
         */
        this.comment = undefined;
        /**
         * The list of unique identifiers for Requirements.
         * Required: false
         * Array: true
         */
        this.requirementCds = undefined;
        /**
         * The list of codes that are associated with the states where the equipment is used.
         * Required: false
         * Array: true
         */
        this.stateOpsCds = undefined;
        /**
         * This indicates whether the search result should provide only a list of unique identifiers for equipment that match the filter criteria.
         * Required: false
         * Array: false
         */
        this.massUpdateInd = undefined;
        /**
         * The list of unique identifiers for Equipment which are to be excluded from the mass update.
         * Required: false
         * Array: true
         */
        this.excludedEquipmentNbrs = undefined;
        /**
         * The list of years that the equipment is scheduled to be retired.
         * Required: false
         * Array: true
         */
        this.scheduledRetireYearYyyys = undefined;
        /**
         * The list of years that the equipment is planned to be purchased.
         * Required: false
         * Array: true
         */
        this.planPurchaseYearYyyys = undefined;
        /**
         * The list of service center identification codes for the locations where the equipment is serviced.
         * Required: false
         * Array: true
         */
        this.servicedBySicCds = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
class ListEquipmentResp {
    constructor() {
        /**
         * The equipment records that matched the search criteria.
         * Required: false
         * Array: true
         */
        this.equipment = undefined;
        /**
         * The details for a License Requirement.
         * Required: false
         * Array: false
         */
        this.requirement = undefined;
        /**
         * The codes associated with the states where the equipment is used.
         * Required: false
         * Array: true
         */
        this.stateOpsCds = undefined;
        /**
         * The list of unique identifiers for Equipment which matched the filter criteria.
         * Required: false
         * Array: true
         */
        this.equipmentNbrs = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Path parameter used to retrieve the list of EquipmentComment records.
 */
class ListEquipmentCommentsPath {
    constructor() {
        /**
         * The unique identifier for the Equipment record.
         * Required: true
         * Array: false
         */
        this.equipmentNbr = undefined;
    }
}
/**
 * Query parameter used to retreive the list of EquipmentComment records.
 */
class ListEquipmentCommentsQuery {
    constructor() {
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Response to the request to list equipment comments based on the equipmentNbr provided.
 */
class ListEquipmentCommentsResp {
    constructor() {
        /**
         * The equipment comment records associated with the equipmentNbr.
         * Required: false
         * Array: true
         */
        this.equipmentComment = undefined;
    }
}
/**
 * Path parameters used to retrieve the list of Equipment field change history records.
 */
class ListEquipmentFieldChangeHistoryPath {
    constructor() {
        /**
         * The unique identifier for the Equipment record.
         * Required: true
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         * The name of the Equipment field whose changes will be listed.
         * Required: true
         * Array: false
         */
        this.fieldName = undefined;
    }
}
/**
 * Query parameter used to retreive the list of Equipment field change history records.
 */
class ListEquipmentFieldChangeHistoryQuery {
    constructor() {
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Response to the request to list Equipment field change history records based on the equipmentNbr and fieldName provided.
 */
class ListEquipmentFieldChangeHistoryResp {
    constructor() {
        /**
         * The list of changes that were made to a field that belongs to an Equipment record.
         * Required: false
         * Array: true
         */
        this.fieldValueChanges = undefined;
    }
}
/**
 * Path parameter used to retrieve the list of Equipment History records.
 */
class ListEquipmentHistoryPath {
    constructor() {
        /**
         * The unique identifier for the Equipment record.
         * Required: true
         * Array: false
         */
        this.equipmentNbr = undefined;
    }
}
/**
 * Query parameter used to retreive the list of Equipment History records.
 */
class ListEquipmentHistoryQuery {
    constructor() {
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Response to the request to list equipment history records based on the equipmentNbr provided.
 */
class ListEquipmentHistoryResp {
    constructor() {
        /**
         * The list of equipment history records associated with the equipmentNbr provided.
         * Required: false
         * Array: true
         */
        this.equipmentHistoryRecords = undefined;
    }
}
/**
 *
 */
class ListFleetCdsRqst {
    constructor() {
        /**
         * The unique identifier for a Fleet Code.
         * Required: false
         * Array: false
         */
        this.fleetCd = undefined;
        /**
         * The description of the Fleet Code.
         * Required: false
         * Array: false
         */
        this.description = undefined;
        /**
         * The code associated with the business unit.  E.g. CCX legacy Con-way.
         * Required: false
         * Array: false
         */
        this.corpCd = undefined;
        /**
         * The code associated with the state where the fleet mainly operates.
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         * The code associated with the plate type.  Permitted values: A (Annual), I (Interstate), P (Permanent), and M (Multiyear).
         * Required: false
         * Array: false
         */
        this.plateTypeCd = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 *
 */
class ListFleetCdsResp {
    constructor() {
        /**
         * The list of Fleet Codes which matched the filter criteria.
         * Required: false
         * Array: true
         */
        this.fleetCodes = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 *
 */
class ListHistoryForLicenseCdPath {
    constructor() {
        /**
         * The unique identifier for a License Code.
         * Required: true
         * Array: false
         */
        this.licenseCd = undefined;
    }
}
/**
 *
 */
class ListHistoryForLicenseCdQuery {
    constructor() {
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 *
 */
class ListHistoryForLicenseCdResp {
    constructor() {
        /**
         * The License Code Hist records which are associated with the licenseCd.
         * Required: false
         * Array: true
         */
        this.licenseCodeHists = undefined;
        /**
         * The message that is returned which describes what the result of the list was.
         * Required: false
         * Array: false
         */
        this.resultMessage = undefined;
    }
}
/**
 *
 */
class ListHistoryForLicenseCdFieldChangePath {
    constructor() {
        /**
         * The unique identifier for a License Code.
         * Required: true
         * Array: false
         */
        this.licenseCd = undefined;
        /**
         * The name of the License Code field whose changes will be listed.
         * Required: true
         * Array: false
         */
        this.fieldName = undefined;
    }
}
/**
 *
 */
class ListHistoryForLicenseCdFieldChangeQuery {
    constructor() {
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 *
 */
class ListHistoryForLicenseCdFieldChangeResp {
    constructor() {
        /**
         * The list of changes that were made to a field that belongs to a License Code record.
         * Required: false
         * Array: true
         */
        this.fieldValueChanges = undefined;
    }
}
class ListIncompleteRequirementProjectsResp {
    constructor() {
        /**
         * The list of incomplete Requirement Projects.
         * Required: false
         * Array: true
         */
        this.incompleteRequirementProjects = undefined;
    }
}
/**
 *
 */
class ListLicenseCdsRqst {
    constructor() {
        /**
         * The list of unique identifiers for License Codes.
         * Required: true
         * Array: true
         */
        this.licenseCds = undefined;
        /**
         * The codes associated with the corporation names. E.g. RSI and MLG (Menlo legacy).
         * Required: false
         * Array: true
         */
        this.corpCds = undefined;
        /**
         * The codes associated with the states for the licenses.
         * Required: false
         * Array: true
         */
        this.domicileStateCds = undefined;
        /**
         * The identifiers of the fleet codes.
         * Required: false
         * Array: true
         */
        this.licFleetCodeIds = undefined;
        /**
         * The codes associated with the types of vehicles.  Permitted values: 'P' for power unit or 'T' for trailer.
         * Required: false
         * Array: true
         */
        this.vehicleTypeCds = undefined;
        /**
         * The descriptions of the License Codes.
         * Required: false
         * Array: true
         */
        this.descriptions = undefined;
        /**
         * The codes associated with the license requirements.
         * Required: false
         * Array: true
         */
        this.requirementCds = undefined;
        /**
         * The codes associated with the states where the license vehicles will operate.
         * Required: false
         * Array: true
         */
        this.stateOps = undefined;
        /**
         * This indicates whether the search result should provide only a list of unique identifiers for license codes that match the filter criteria.
         * Required: false
         * Array: false
         */
        this.massUpdateInd = undefined;
        /**
         * The list of unique identifiers for License Codes which are to be excluded from the mass update.
         * Required: false
         * Array: true
         */
        this.excludedLicenseCds = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 *
 */
class ListLicenseCdsResp {
    constructor() {
        /**
         * The list of License Codes which matched the filter criteria.
         * Required: false
         * Array: true
         */
        this.licenseCodes = undefined;
        /**
         * The list of unique identifiers for License Codes which matched the filter criteria.
         * Required: false
         * Array: true
         */
        this.licenseCds = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 *
 */
class ListRequirementCommentsPath {
    constructor() {
        /**
         * The unique identifier for the Requirement.
         * Required: true
         * Array: false
         */
        this.requirementCd = undefined;
    }
}
/**
 *
 */
class ListRequirementCommentsResp {
    constructor() {
        /**
         * The comments associated with the requirementCd provided.
         * Required: false
         * Array: true
         */
        this.requirementComments = undefined;
    }
}
/**
 *
 */
class ListRequirementsRqst {
    constructor() {
        /**
         * The unique identifier for a Requirement.
         * Required: false
         * Array: false
         */
        this.requirementCd = undefined;
        /**
         * The name of the Requirement.
         * Required: false
         * Array: false
         */
        this.requirementName = undefined;
        /**
         * The code associated with the state for the requirement.
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         * The date on which the Requirement expires.
         * Required: false
         * Array: false
         */
        this.expireDate = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 *
 */
class ListRequirementsResp {
    constructor() {
        /**
         * The list of Requirements which matched the filter criteria.
         * Required: false
         * Array: true
         */
        this.requirements = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 *
 */
class ListRequirementProjectsPath {
    constructor() {
        /**
         * The unique identifier for the Requirement.
         * Required: true
         * Array: false
         */
        this.requirementCd = undefined;
    }
}
/**
 *
 */
class ListRequirementProjectsResp {
    constructor() {
        /**
         * The list of Requirement Projects.
         * Required: false
         * Array: true
         */
        this.requirementProjects = undefined;
    }
}
class ListStateWeightGroupHeadersResp {
    constructor() {
        /**
         * The list of State Weight Group headers.
         * Required: false
         * Array: true
         */
        this.stateWeightGroupHeaders = undefined;
    }
}
/**
 *
 */
class ListStateWeightGroupsResp {
    constructor() {
        /**
         * The list of the State Weight Groups.
         * Required: false
         * Array: true
         */
        this.stateWeightGroups = undefined;
    }
}
/**
 *
 */
class ListUploadControlCsvsResp {
    constructor() {
        /**
         * The list of UploadControlCsvs that was returned.
         * Required: false
         * Array: true
         */
        this.uploadControlCsvs = undefined;
    }
}
class ListUploadCsvRecErrorsPath {
    constructor() {
        /**
         * The unique identifier that is assigned by the system to a CSV file upload when it occurs and the metadata row is created.
         * Required: true
         * Array: false
         */
        this.uploadId = undefined;
    }
}
class ListUploadCsvRecErrorsResp {
    constructor() {
        /**
         * The list of CSV error records for the requested upload.
         * Required: false
         * Array: true
         */
        this.uploadCsvRecErrors = undefined;
    }
}
/**
 *
 */
class UpdateCorpCodeRequirementProjectRqst {
    constructor() {
        /**
         * The Corp Code Requirement Project details which will be used to perform the update.
         * Required: true
         * Array: false
         */
        this.corpCodeRequirementProject = undefined;
    }
}
/**
 *
 */
class UpdateCorpCodeRequirementProjectResp {
    constructor() {
        /**
         * The message that is returned which describes what the result of the update was.
         * Required: false
         * Array: false
         */
        this.resultMessage = undefined;
    }
}
class UpdateEquipmentRqst {
    constructor() {
        /**
         * List of Equipment numbers to be updated.
         * Required: true
         * Array: true
         */
        this.equipmentNbrs = undefined;
        /**
         * Equipment object to be used as model for mass update.
         * Required: true
         * Array: false
         */
        this.equipment = undefined;
        /**
         * The comment to add in all update references.
         * Required: false
         * Array: false
         */
        this.comment = undefined;
        /**
         * The standard audit trail information.
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
class UpdateEquipmentResp {
    constructor() {
        /**
         * The message that is returned which describes what the result of the update was.
         * Required: false
         * Array: false
         */
        this.resultMessage = undefined;
    }
}
/**
 * Request to update the equipment comment.
 */
class UpdateEquipmentCommentRqst {
    constructor() {
        /**
         * The new EquipmentComment values that will be used to update the existing entry.
         * Required: true
         * Array: false
         */
        this.equipmentComment = undefined;
    }
}
/**
 *
 */
class UpdateFleetCdRqst {
    constructor() {
        /**
         * The Fleet Code details which will be used to perform the update.
         * Required: true
         * Array: false
         */
        this.fleetCode = undefined;
    }
}
/**
 *
 */
class UpdateFleetCdResp {
    constructor() {
        /**
         * The message that is returned which describes what the result of the update was.
         * Required: false
         * Array: false
         */
        this.resultMessage = undefined;
    }
}
class UpdateLicenseCdsRqst {
    constructor() {
        /**
         * The list of unique identifiers for the License Codes that will be updated.
         * Required: true
         * Array: true
         */
        this.licenseCds = undefined;
        /**
         * The License Code details which will be used to perform the update.
         * Required: true
         * Array: false
         */
        this.licenseCode = undefined;
        /**
         * This indicates whether the operation is a mass update; if not set, then the default operation is a single update.
         * Required: false
         * Array: false
         */
        this.massUpdateInd = undefined;
        /**
         * The list of License Code Requirements to be deleted from the License Code.
         * Required: false
         * Array: true
         */
        this.licenseCodeRequirementDeletions = undefined;
        /**
         * The list of License Code State Operations to be deleted from the License Code.
         * Required: false
         * Array: true
         */
        this.licenseCodeStateOperationsDeletions = undefined;
        /**
         * The list of License Code States to be deleted from the License Code.
         * Required: false
         * Array: true
         */
        this.licenseCodeStateDeletions = undefined;
    }
}
class UpdateLicenseCdsResp {
    constructor() {
        /**
         * The message that is returned which describes what the result of the update was.
         * Required: false
         * Array: false
         */
        this.resultMessage = undefined;
    }
}
/**
 *
 */
class UpdateRequirementRqst {
    constructor() {
        /**
         * The Requirement details which will be used to perform the update.
         * Required: true
         * Array: false
         */
        this.requirement = undefined;
    }
}
/**
 *
 */
class UpdateRequirementResp {
    constructor() {
        /**
         * The message that is returned which describes what the result of the update was.
         * Required: false
         * Array: false
         */
        this.resultMessage = undefined;
    }
}
/**
 *
 */
class UpdateRequirementCommentRqst {
    constructor() {
        /**
         * The Requirement Comment details which will be used to perform the update.
         * Required: true
         * Array: false
         */
        this.requirementComment = undefined;
    }
}
/**
 *
 */
class UpdateRequirementCommentResp {
    constructor() {
        /**
         * The Requirement Comment details after the update was performed.
         * Required: false
         * Array: false
         */
        this.requirementComment = undefined;
        /**
         * The message that is returned which describes what the result of the update was.
         * Required: false
         * Array: false
         */
        this.resultMessage = undefined;
    }
}
/**
 *
 */
class UpdateRequirementProjectRqst {
    constructor() {
        /**
         * The Requirement Project details which will be used to perform the update.
         * Required: true
         * Array: false
         */
        this.requirementProject = undefined;
    }
}
/**
 *
 */
class UpdateRequirementProjectResp {
    constructor() {
        /**
         * The message that is returned which describes what the result of the update was.
         * Required: false
         * Array: false
         */
        this.resultMessage = undefined;
    }
}
/**
 *
 */
class UpdateStateWeightGroupsRqst {
    constructor() {
        /**
         * The list of state weight groups for which the weight limit is being changed.
         * Required: true
         * Array: true
         */
        this.stateWeightGroups = undefined;
    }
}
/**
 *
 */
class UpdateStateWeightGroupsResp {
    constructor() {
        /**
         * The message that is returned which describes what the result of the update was.
         * Required: false
         * Array: false
         */
        this.resultMessage = undefined;
    }
}
/**
 *
 */
class UpdateUploadControlCsvRqst {
    constructor() {
        /**
         * The UploadControlCsv to be updated.
         * Required: true
         * Array: false
         */
        this.uploadControlCsv = undefined;
    }
}
/**
 *
 */
class UpdateUploadControlCsvResp {
    constructor() {
        /**
         * The UploadControlCsv that was updated.
         * Required: false
         * Array: false
         */
        this.uploadControlCsv = undefined;
    }
}
class ValidateLicenseCdsRqst {
    constructor() {
        /**
         * The list of license codes to be validated.
         * Required: true
         * Array: true
         */
        this.licenseCds = undefined;
    }
}
class ValidateLicenseCdsResp {
    constructor() {
        /**
         * The list of invalid license codes and their associated error messages.
         * Required: false
         * Array: true
         */
        this.licenseCodeErrors = undefined;
    }
}
/**
 * Enter comments
 */
class ListEnsembleConfigurationsResp {
    constructor() {
        /**
         * List of message queues - AQ and JMS
         * Required: false
         * Array: true
         */
        this.queues = undefined;
        /**
         * List of subscribers - Pub/Sub
         * Required: false
         * Array: true
         */
        this.subscriptions = undefined;
        /**
         * List of API channels
         * Required: false
         * Array: true
         */
        this.apiChannels = undefined;
        /**
         * List of API channels
         * Required: false
         * Array: true
         */
        this.timers = undefined;
        /**
         * List of API channels
         * Required: false
         * Array: true
         */
        this.emails = undefined;
        /**
         * List of API channels
         * Required: false
         * Array: true
         */
        this.files = undefined;
        /**
         * List of API channels
         * Required: false
         * Array: true
         */
        this.jmsQueues = undefined;
    }
}
class TimerConfig {
    constructor() {
        /**
         * Name of the timer.
         * Required: true
         * Array: false
         */
        this.name = undefined;
        /**
         * Interval for timer. Specify in seconds. The value is set in Octopus using the property sym.timer.<name>.intervalInSeconds
         * Required: true
         * Array: false
         */
        this.intervalInSeconds = undefined;
        /**
         * Specify the timer behavior. If fixedRate is set to true, the timer will fire an ensemble once every interval regardless of whether previous tasks are executing.
         * 						If fixedRate is set to false, the timer will be scheduled to fire after the specified interval, once the current execution is complete.
         * 						For both settings, it does not matter whether the ensemble execution is successful or not for the next timer event to be fired.
         * Required: true
         * Array: false
         */
        this.fixedRate = undefined;
        /**
         * An expression similar to crontab. Expression = 'minute hour dayOfMonth month dayOfWeek'.
         * 						Minute = 0-59, hour = 0-23, dayOfMonth = 1-31, month = [1-12 or JAN, FEB, ..., DEC],  dayOfWeek = [0-6 OR SUN-SAT].
         * 						[*] = any values, [,] = list of values, [-] = range of values, [/] = step values
         * Required: true
         * Array: false
         */
        this.expression = undefined;
    }
}
class Timer {
    constructor() {
        /**
         * Configuration of the timer.
         * Required: true
         * Array: false
         */
        this.configuration = undefined;
        /**
         * Indicates whether the timer is running. True indicates timer is running
         * Required: true
         * Array: false
         */
        this.runningInd = undefined;
    }
}
/**
 * Path parameters for listTimerEnsembles
 */
class ListTimerEnsemblesPath {
    constructor() {
        /**
         * The name of the timer ensemble from which the list of timers is requested
         * Required: false
         * Array: false
         */
        this.name = undefined;
    }
}
/**
 * Response for listTimerEnsembles
 */
class ListTimerEnsemblesResp {
    constructor() {
        /**
         * List of timers defined in this ensemble.
         * Required: false
         * Array: true
         */
        this.timers = undefined;
    }
}
/**
 * Path parameters for startTimerEnsemble
 */
class StartTimerEnsemblePath {
    constructor() {
        /**
         * The name of the timer to start
         * Required: false
         * Array: false
         */
        this.name = undefined;
    }
}
/**
 * Response for startTimerEnsemble
 */
class StartTimerEnsembleResp {
    constructor() {
        /**
         * Indicates whether the timer is running. True indicates timer is running
         * Required: false
         * Array: false
         */
        this.runningInd = undefined;
    }
}
/**
 * Path parameters for stopTimerEnsemble
 */
class StopTimerEnsemblePath {
    constructor() {
        /**
         * The name of the timer to stop
         * Required: false
         * Array: false
         */
        this.name = undefined;
    }
}
/**
 * Response for stopTimerEnsemble
 */
class StopTimerEnsembleResp {
    constructor() {
        /**
         * Indicates whether the timer is running. True indicates timer is running
         * Required: false
         * Array: false
         */
        this.runningInd = undefined;
    }
}
/**
 * Request for startLicMaintChEnsemble
 */
class StartLicMaintChEnsembleRqst {
    constructor() {
        /**
         * The name of the ensemble to start
         * Required: false
         * Array: false
         */
        this.ensembleName = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * The payload for the ensemble process
         * Required: false
         * Array: false
         */
        this.payload = undefined;
    }
}
/**
 * Path parameters for startLicMaintChEnsemble
 */
class StartLicMaintChEnsemblePath {
    constructor() {
        /**
         * The name of the channel in which this ensemble will be executed
         * Required: false
         * Array: false
         */
        this.channelName = undefined;
    }
}
/**
 * Query parameters for startLicMaintChEnsemble
 */
class StartLicMaintChEnsembleQuery {
    constructor() {
        /**
         * Send true if a synchronous response is required. Normally, channel execute asynchronously.
         * Required: false
         * Array: false
         */
        this.synchronous = undefined;
    }
}
/**
 * Response for startLicMaintChEnsemble
 */
class StartLicMaintChEnsembleResp {
    constructor() {
        /**
         * Instance ID of the ensemble that was started.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
    }
}
/**
 * The basic key information that is needed for searching Equipment Licensing events.
 */
class EquipmentLicensingEventPayload extends PayloadType {
    constructor() {
        super(...arguments);
        /**
         * The List of Equipment details which will be used as a model for the bulk upsert.
         * Required: false
         * Array: true
         */
        this.equipment = undefined;
    }
}
/**
 * This type will be used to publish documents targeted for Elastic Search on a Kafka topic. The type includes KafkaMessageHeader.
 */
class EnsembleStateSearchRecordMessage extends EnsembleStateSearchRecord {
    constructor() {
        super(...arguments);
        /**
         * Additional details about the document
         * Required: false
         * Array: false
         */
        this.messageHeader = undefined;
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */

export { SdkEquipmentLicensingModule, EquipmentLicensingApiService, BulkRestartEnsembleRqst, BulkRestartEnsembleResp, BulkResumeEnsembleRqst, BulkResumeEnsembleResp, GetEnsembleStatusCountsPath, GetEnsembleStatusCountsResp, GetQueuePendingCountPath, GetQueuePendingCountResp, RestartEnsemblePath, RestartEnsembleRqst, RestartEnsembleResp, ResumeEnsemblePath, ResumeEnsembleRqst, ResumeEnsembleResp, BulkResumeEnsemblePath, BulkRestartEnsemblePath, StartMessageDrivenEnsembleRqst, StartMessageDrivenEnsembleResp, KillEnsembleRqst, KillEnsembleResp, StopQueuePath, StopQueueWebhookPath, StopQueueWebhookQuery, StartQueuePath, ListQueuesResp, ListExpiredMessagesPath, ListExpiredMessagesResp, DeleteExpiredMessagesPath, DeleteExpiredMessagesQuery, DeleteExpiredMessagesResp, ReplayMessagePath, GetPubSubStatusCountsPath, GetPubSubStatusCountsResp, StartSubscriptionPath, StopSubscriptionPath, GetUndeliveredMessagesPath, GetUndeliveredMessagesResp, ListSubscriptionsResp, GetSubscriptionPath, GetSubscriptionResp, StartChannelPath, StopChannelPath, ListChannelsResp, MessageDrivenEnsemblePayload, DynamicMessageDrivenPayload, TransactionEventEnsemblePayload, DeleteExpiredMessageStatus, KillEnsembleStatus, EnsembleStateSearchRecord, Payload, EnsembleState, NestedEnsembleState, EnsembleExecutionContext, MinuetState, EnsembleMetrics, PubSubMetrics, QueueMetrics, AuxMessagePublishers, EnsembleConfiguration, Subscription, Channel, LTLEventEquipmentLicensing, BulkUpdateResp, BulkUpdateItemError, BulkApiRespItem, EsAggregationFieldRqst, EsAggregationDateRange, EsDynamicPivotRqst, EsAggregationRqst, EsAggregationFieldResp, EsAggregationResp, EnsembleStateSearchFilterNot, EnsembleStateSearchFilterOr, EnsembleStateSearchFilterAnd, EnsembleStateSearchFilter, GetElasticDocumentEnsembleStatePath, GetElasticDocumentEnsembleStateQuery, GetElasticDocumentEnsembleStateResp, GetElasticDocumentEnsembleStateCountQuery, GetElasticDocumentEnsembleStateCountResp, UpdateElasticDocumentEnsembleStateRqst, UpdateElasticDocumentEnsembleStatePath, UpdateElasticDocumentEnsembleStateResp, BulkUpdateElasticDocumentEnsembleStateResp, DeleteElasticDocumentEnsembleStatePath, DeleteElasticDocumentEnsembleStateResp, SearchEnsembleStateUnfilteredRqst, SearchEnsembleStateUnfilteredResp, ExportEnsembleStateUnfilteredRqst, RegisterFilterEnsembleStateUnfilteredRqst, RegisterFilterEnsembleStateUnfilteredResp, UnregisterFilterEnsembleStateUnfilteredRqst, ChangesFeedEnsembleStateUnfilteredRqst, ListAggregationsEnsembleStateUnfilteredRqst, ListAggregationsEnsembleStateUnfilteredResp, EquipmentValidationError, LicenseCodeError, LicMaintPayload, StateWeightGroupComparison, StateWeightGroupHeader, Corp, CorpCodeRequirementProject, CorpCodeRequirementProjectHist, Equipment, EquipmentComment, EquipmentCsv, EquipmentCsvException, EquipmentCsvExceptionHist, EquipmentCsvHist, EquipmentHist, FleetCode, FleetCodeHist, InterfaceEmployee, InterfaceEmployeeSnapshot, LicenseCode, LicenseCodeHist, LicenseCodeRequirement, LicenseCodeRequirementHist, LicenseCodeState, LicenseCodeStateHist, LicenseCodeStateOperations, LicenseCodeStateOperationsHist, PlateHist, Requirement, RequirementComment, RequirementCommentHist, RequirementHist, RequirementProject, RequirementProjectHist, StateWeightGroup, UploadControlCsv, UploadControlCsvHist, UploadCsvRecError, EventWait, RetriggerEvent, SymphonyMessage, SymphonyQueue, TransactionEvent, TransactionEventBusiness, TransactionEventDetail, BulkUpsertEquipmentRqst, BulkUpsertEquipmentResp, CompareLicenseCodesRqst, CompareLicenseCodesResp, CompareStateWeightGroupsRqst, CompareStateWeightGroupsResp, CreateCorpRqst, CreateCorpResp, CreateCorpCodeRequirementProjectRqst, CreateCorpCodeRequirementProjectResp, CreateEquipmentRqst, CreateEquipmentResp, CreateEquipmentCommentsRqst, CreateEquipmentCommentsResp, CreateFleetCdRqst, CreateFleetCdResp, CreateLicenseCdRqst, CreateLicenseCdResp, CreateRequirementRqst, CreateRequirementResp, CreateRequirementCommentRqst, CreateRequirementCommentResp, CreateRequirementProjectRqst, CreateRequirementProjectResp, CreateUploadControlCsvRqst, CreateUploadControlCsvResp, CreateUploadCsvRecErrorsRqst, CreateUploadCsvRecErrorsResp, DeleteEquipmentPath, DeleteEquipmentCommentPath, DeleteFleetCdPath, DeleteLicenseCdPath, DeleteRequirementPath, DeleteRequirementCommentPath, DeleteRequirementProjectPath, GetDomicileStateAndWeightGroupsPath, GetDomicileStateAndWeightGroupsResp, GetEquipmentPath, GetEquipmentResp, GetEquipmentCommentPath, GetEquipmentCommentResp, GetCorpPath, GetCorpResp, GetCorpCodeRequirementProjectPath, GetCorpCodeRequirementProjectResp, GetFleetCdPath, GetFleetCdResp, GetLicenseCdPath, GetLicenseCdResp, GetRequirementPath, GetRequirementResp, GetRequirementProjectPath, GetRequirementProjectResp, ListCorpsResp, ListEquipmentRqst, ListEquipmentResp, ListEquipmentCommentsPath, ListEquipmentCommentsQuery, ListEquipmentCommentsResp, ListEquipmentFieldChangeHistoryPath, ListEquipmentFieldChangeHistoryQuery, ListEquipmentFieldChangeHistoryResp, ListEquipmentHistoryPath, ListEquipmentHistoryQuery, ListEquipmentHistoryResp, ListFleetCdsRqst, ListFleetCdsResp, ListHistoryForLicenseCdPath, ListHistoryForLicenseCdQuery, ListHistoryForLicenseCdResp, ListHistoryForLicenseCdFieldChangePath, ListHistoryForLicenseCdFieldChangeQuery, ListHistoryForLicenseCdFieldChangeResp, ListIncompleteRequirementProjectsResp, ListLicenseCdsRqst, ListLicenseCdsResp, ListRequirementCommentsPath, ListRequirementCommentsResp, ListRequirementsRqst, ListRequirementsResp, ListRequirementProjectsPath, ListRequirementProjectsResp, ListStateWeightGroupHeadersResp, ListStateWeightGroupsResp, ListUploadControlCsvsResp, ListUploadCsvRecErrorsPath, ListUploadCsvRecErrorsResp, UpdateCorpCodeRequirementProjectRqst, UpdateCorpCodeRequirementProjectResp, UpdateEquipmentRqst, UpdateEquipmentResp, UpdateEquipmentCommentRqst, UpdateFleetCdRqst, UpdateFleetCdResp, UpdateLicenseCdsRqst, UpdateLicenseCdsResp, UpdateRequirementRqst, UpdateRequirementResp, UpdateRequirementCommentRqst, UpdateRequirementCommentResp, UpdateRequirementProjectRqst, UpdateRequirementProjectResp, UpdateStateWeightGroupsRqst, UpdateStateWeightGroupsResp, UpdateUploadControlCsvRqst, UpdateUploadControlCsvResp, ValidateLicenseCdsRqst, ValidateLicenseCdsResp, ListEnsembleConfigurationsResp, TimerConfig, Timer, ListTimerEnsemblesPath, ListTimerEnsemblesResp, StartTimerEnsemblePath, StartTimerEnsembleResp, StopTimerEnsemblePath, StopTimerEnsembleResp, StartLicMaintChEnsembleRqst, StartLicMaintChEnsemblePath, StartLicMaintChEnsembleQuery, StartLicMaintChEnsembleResp, EquipmentLicensingEventPayload, EnsembleStateSearchRecordMessage };

